/**
 * Türkçe — the console's default language.
 *
 * Keys mirror `en.ts` exactly. `_one` entries are deliberately absent: Turkish
 * does not pluralise a noun after a number ("3 kullanıcı", not "3 kullanıcılar"),
 * so the shared form is already correct for every count.
 */
export const tr: Record<string, string> = {
  // ── Ortak ─────────────────────────────────────────────────────────────────
  'common.any': 'Hepsi',
  'common.yes': 'Evet',
  'common.no': 'Hayır',
  'common.on': 'Açık',
  'common.off': 'Kapalı',
  'common.cancel': 'Vazgeç',
  'common.confirm': 'Onayla',
  'common.save': 'Kaydet',
  'common.saveChanges': 'Değişiklikleri kaydet',
  'common.saveTheseChanges': 'Bu değişiklikler kaydedilsin mi?',
  'common.delete': 'Sil',
  'common.restore': 'Geri getir',
  'common.remove': 'Kaldır',
  'common.close': 'Kapat',
  'common.loading': 'Yükleniyor',
  'common.loadingEllipsis': 'Yükleniyor…',
  'common.dismiss': 'Kapat',
  'common.searchPlaceholder': 'Ara…',
  'common.previous': 'Önceki',
  'common.next': 'Sonraki',
  'common.none': 'Yok',
  'common.never': 'Hiçbir zaman',
  'common.always': 'Her zaman',
  'common.noLimit': 'Sınır yok',
  'common.noCap': 'Üst sınır yok',
  'common.name': 'Ad',
  'common.description': 'Açıklama',
  'common.enabled': 'Etkin',
  'common.disabled': 'Devre dışı',
  'common.priority': 'Öncelik',
  'common.template': 'Şablon',
  'common.selectTemplate': 'Bir şablon seçin…',
  'common.title': 'Başlık',
  'common.body': 'Metin',
  'common.language': 'Dil',
  'common.userId': 'Kullanıcı kimliği',
  'common.deviceId': 'Cihaz kimliği',
  'common.pasteDeviceId': 'Bir cihaz kimliği yapıştırın',
  'common.somethingWentWrong': 'Bir şeyler ters gitti',
  'common.nothingToShow': 'Gösterilecek bir şey yok',
  'common.done': 'Tamam — işlem kaydına yazıldı',
  'common.reason': 'Gerekçe',
  'common.reasonHint': 'Adınızla birlikte işlem kaydına yazılır',
  'common.reasonPlaceholder': 'Bunu neden yapıyorsunuz?',
  'common.results': '{count} sonuç',
  'common.pageOf': 'Sayfa {page} / {totalPages}',
  'common.requestFailed': 'İstek başarısız oldu ({status})',
  'common.users': '{count} kullanıcı',
  'common.creditSuffix': 'kredi',
  'common.available': 'Kullanılabilir:',

  // ── Etkinlik dökümü ───────────────────────────────────────────────────────
  'activity.metric.top_up': 'Yüklenen krediler',
  'activity.metric.premium': "Premium'a geçenler",
  'activity.metric.revenue': 'Tahsil edilen ödemeler',
  'activity.metric.new_user': 'Yeni kullanıcılar',
  'activity.metric.woke_up': 'Kalkanlar',
  'activity.metric.missed': 'Görevi kaçıranlar',
  'activity.metric.forfeited': 'Yakılan kilitler',
  'activity.metric.payout': 'Dağıtılan ödüller',
  'activity.metric.spent': 'Harcanan krediler',
  'activity.metric.adjusted': 'Elle düzeltilen krediler',

  'activity.about.top_up':
    'Cüzdana eklenen krediler — satın alınan, tanımlanan ya da reklamdan kazanılan.',
  'activity.about.premium':
    'Satın alınan Premium dönemleri — kartla ya da günışığıyla. Yönetici ' +
    'tanımlamaları bunun yerine etkinlik kaydında görünür.',
  'activity.about.revenue': 'İadeler düşüldükten sonra bankadan geçen para.',
  'activity.about.new_user': 'Açılan hesaplar.',
  'activity.about.woke_up': 'Süre dolmadan geçilen görevler.',
  'activity.about.missed': 'Zamanında geçilemeyen görevler.',
  'activity.about.forfeited': 'Kaçırılan görev yüzünden yakılan kilitler.',
  'activity.about.payout': 'Dağıtımdan cüzdana geçen paylar.',
  'activity.about.spent': 'Uygulama içinde harcanan krediler.',
  'activity.about.adjusted': 'Bir yöneticinin elle hareket ettirdiği krediler.',

  'activity.scope.today': 'Gece yarısından beri.',
  'activity.scope.window': 'Son {days} gün içinde.',

  'activity.total.users': 'Kişi',
  'activity.total.credits': 'Kredi',
  'activity.total.money': 'Tutar',
  'activity.count.top_up': 'Yükleme',
  'activity.count.premium': 'Dönem',
  'activity.count.revenue': 'Ödeme',
  'activity.count.new_user': 'Kayıt',
  'activity.count.woke_up': 'Kazanılan',
  'activity.count.missed': 'Kaçırılan',
  'activity.count.forfeited': 'Yakılan',
  'activity.count.payout': 'Dağıtım',
  'activity.count.spent': 'Harcama',
  'activity.count.adjusted': 'Düzeltme',

  'activity.times': '{count} ×',
  'activity.premium': 'Premium',
  'activity.deleted': 'Silinmiş',
  'activity.empty': 'Henüz kimse yok',
  'activity.emptyDescription': 'Bu aralıkta bu türden bir şey olmadı.',

  // ── Kabuk ─────────────────────────────────────────────────────────────────
  'nav.brand': 'Alarm Konsolu',
  'nav.dashboard': 'Panel',
  'nav.users': 'Kullanıcılar',
  'nav.alarms': 'Alarmlar',
  'nav.rewards': 'Ödüller',
  'nav.notifications': 'Bildirimler',
  'nav.conversions': 'Mod geçişleri',
  'nav.activity': 'İşlem kaydı',
  'nav.backTo.user': 'Kullanıcıya dön',
  'nav.backTo.alarm': 'Alarma dön',
  'nav.backTo.event': 'Olaya dön',
  'nav.open': 'Menüyü aç',
  'nav.close': 'Menüyü kapat',
  'nav.signedInAs': 'Oturum açan:',
  'nav.light': 'Açık',
  'nav.dark': 'Koyu',
  'nav.signOut': 'Çıkış yap',
  'nav.language': 'Dil',

  // ── Giriş ─────────────────────────────────────────────────────────────────
  'login.subtitle':
    'Kullanıcıları, alarmları ve ödülleri yönetmek için giriş yapın',
  'login.username': 'Kullanıcı adı',
  'login.password': 'Parola',
  'login.submit': 'Giriş yap',
  'login.note': 'Giriş bilgileri sunucu ortamında tanımlanır.',

  // ── Panel ─────────────────────────────────────────────────────────────────
  'dashboard.title': 'Panel',
  'dashboard.description': 'Bugün şu ana kadar ve son {days} gün',
  'dashboard.policy':
    'Çalma başına {cost} kilit · ödüller {window} · {notice} önce kurulur',
  'dashboard.attention.claims': 'İnceleme bekleyen ödül talepleri',
  'dashboard.attention.claimsCaption':
    'Karar verilene kadar kilitler tutulur — Alarmlar → İnceleme kuyruğu',
  'dashboard.attention.jobs': 'Zamanı geçmiş bildirimler',
  'dashboard.attention.jobsCaption':
    'Kuyruk geride kaldı — Bildirimler → Kuyruk',

  'dashboard.morning.title': 'Bu sabah',
  'dashboard.morning.description':
    'Gece yarısından beri, sizin saat diliminizde',
  'dashboard.morning.settled': '{days} günde {count} tanesi sonuçlandı',
  'dashboard.morning.wokeUp': 'Kalkanlar',
  'dashboard.morning.wokeUpCaption': 'sonuçlanan çalmaların {percent} kadarı',
  'dashboard.morning.noRings': 'Henüz sonuçlanan çalma yok',
  'dashboard.morning.missed': 'Kaçıranlar',
  'dashboard.morning.missedCaption': 'sonuçlananların {percent} kadarı',
  'dashboard.morning.rings': 'Çalan alarmlar',
  'dashboard.morning.ringsCaption': '{count} tanesi çözülmeden sustu',
  'dashboard.morning.solve': 'Ortalama çözüm',
  'dashboard.morning.solveCaption': 'Görevi geçme süresi',
  'dashboard.morning.wakeRate': '{days} günlük uyanma oranı',
  'dashboard.morning.breakdown':
    '{earned} kazanıldı · {missed} kaçırıldı · {withheld} inceleme bekliyor',

  'dashboard.today.title': 'Bugünün ticareti',
  'dashboard.today.description':
    'Gece yarısından beri para ve üyelik, sizin saat diliminizde',
  'dashboard.today.hint': 'Arkasındaki kişileri görmek için bir sayıya dokunun',
  'dashboard.today.toppedUp': 'Yüklenen kredi',
  'dashboard.today.toppedUpCaption': '{users} kullanıcı · {count} yükleme',
  'dashboard.today.premium': "Premium'a geçen",
  'dashboard.today.premiumCaption': 'şu anda {count} kişi Premium',
  'dashboard.today.revenue': 'Bugün tahsil edilen',
  'dashboard.today.revenueCaption': '{count} ödeme · {users} kullanıcı',
  'dashboard.today.newUsers': 'Yeni kullanıcı',
  'dashboard.today.newUsersCaption': 'bu hafta {count}',

  'dashboard.economy.title': 'Ekonomi',
  'dashboard.economy.description':
    'Şu anki varlıklar ve {days} gün içinde hareket edenler',
  'dashboard.economy.held': 'Kullanıcılardaki krediler',
  'dashboard.economy.heldCaption': 'Sahip oldukları her şey',
  'dashboard.economy.staked': 'Aktif alarmlarda kilitli',
  'dashboard.economy.stakedCaption': 'tüm kredilerin {percent} kadarı',
  'dashboard.economy.won': 'Diğer kullanıcılardan kazanılan',
  'dashboard.economy.wonCaption': 'Tüm zamanların ödemeleri',
  'dashboard.economy.commission': 'Alınan komisyon',
  'dashboard.economy.commissionCaption': 'bu aralıkta {count} dağıtım',
  'dashboard.economy.toppedUp': 'Yüklenen',
  'dashboard.economy.forfeited': 'Yakılan',
  'dashboard.economy.paidOut': 'Dağıtılan',
  'dashboard.economy.adjusted': 'Elle düzeltilen',
  'dashboard.economy.shared':
    '{amount}, {count} kullanıcı arasında paylaştırıldı',
  'dashboard.economy.carryOver': '{amount} sonraki döneme devredildi',

  'dashboard.population.title': 'Kullanıcı kitlesi',
  'dashboard.population.description':
    'Yeni hesaplar ve bunların kaçının alarmı gerçekten çaldı',
  'dashboard.population.premium': 'Premium kullanıcılar',
  'dashboard.population.premiumCaption': 'kullanıcıların {percent} kadarı',
  'dashboard.population.newToday': 'Bugün yeni',
  'dashboard.population.newTodayCaption': 'bu hafta {count}',
  'dashboard.population.total': 'Toplam kullanıcı',
  'dashboard.population.totalCaption': 'bugün {count} tanesi görüldü',
  'dashboard.population.liveAlarms': 'Aktif alarmlar',
  'dashboard.population.liveAlarmsCaption':
    'toplam {total} · {reward} kilit tutuyor',
  'dashboard.population.pushable': 'Bildirimle ulaşılabilir',
  'dashboard.population.pushableCaption': 'kullanıcıların {percent} kadarı',

  'dashboard.charts.wake': 'Kalkma',
  'dashboard.charts.wakeDescription':
    '{days} gün boyunca günlük kazanılan ve kaçırılan görevler',
  'dashboard.charts.credits': 'Hareket eden krediler',
  'dashboard.charts.creditsDescription':
    'Kaçan sabahların aldığı ve dağıtımların geri verdiği',
  'dashboard.series.wokeUp': 'Kalkanlar',
  'dashboard.series.missed': 'Kaçıranlar',
  'dashboard.series.toppedUp': 'Yüklenen',
  'dashboard.series.forfeited': 'Yakılan',
  'dashboard.series.paidOut': 'Dağıtılan',
  'dashboard.series.newUsers': 'Yeni kullanıcılar',
  'dashboard.series.activeUsers': 'Alarmı çalanlar',

  'dashboard.breakdown.platforms': 'Platformlar',
  'dashboard.breakdown.platformsDescription':
    'Cihazlar ve kaçının aboneliği var',
  'dashboard.breakdown.subscribed': 'abone',
  'dashboard.breakdown.challenges': 'Görevler',
  'dashboard.breakdown.challengesDescription':
    'Görev türüne göre alarmlar ve kazandıkları sabahlar',
  'dashboard.breakdown.won': 'kazanıldı',
  'dashboard.breakdown.countries': 'Ülkeler',
  'dashboard.breakdown.countriesDescription': 'Kullanıcılar nerede',
  'dashboard.breakdown.risk': 'Talep riski',
  'dashboard.breakdown.riskDescription':
    'Kuralların {days} gündeki ödül taleplerini nasıl değerlendirdiği',
  'dashboard.breakdown.riskEmpty':
    'Bu aralıkta karara bağlanmış ödül talebi yok.',
  'dashboard.breakdown.empty': 'Henüz kayıt yok.',

  // ── Grafikler ─────────────────────────────────────────────────────────────
  'chart.showChart': 'Grafiği göster',
  'chart.showData': 'Veriyi göster',
  'chart.date': 'Tarih',

  // ── Kullanıcılar ──────────────────────────────────────────────────────────
  'users.title': 'Kullanıcılar',
  'users.description':
    'Her hesap, elindekiler ve nasıl performans gösterdiğiyle birlikte',
  'users.sortBy': 'Sıralama',
  'users.sort.registered': 'Önce en yeni',
  'users.sort.lastSeen': 'Son görülme',
  'users.sort.balance': 'Bakiye',
  'users.sort.earned': 'Toplam kazanç',
  'users.sort.alarms': 'Alarm sayısı',
  'users.sort.wakeRate': 'Uyanma oranı',
  'users.column.account': 'Hesap',
  'users.column.platform': 'Platform',
  'users.column.balance': 'Bakiye',
  'users.column.won': 'Kazanılan',
  'users.column.alarms': 'Alarmlar',
  'users.column.wakeRate': 'Uyanma oranı',
  'users.column.lastSeen': 'Son görülme',
  'users.stakedTitle': '{amount} kilitli',
  'users.stakedInline': '({amount} kilitli)',
  'users.tabsLabel': 'Hesap bölümleri',
  'users.tab.overview': 'Genel bakış',
  'users.tab.alarms': 'Alarmlar',
  'users.tab.wallet': 'Cüzdan',
  'users.tab.payouts': 'Kazançlar',
  'users.deleted': '{when} silindi. Geçmişine dokunulmadı.',
  'users.silencedBy': '{actor} tarafından susturuldu',
  'users.section.wallet': 'Cüzdan',
  'users.row.balance': 'Bakiye',
  'users.row.staked': 'Aktif alarmlarda kilitli',
  'users.row.spendable': 'Harcanabilir',
  'users.row.wonFromSettlements': 'Dağıtımlardan kazanılan',
  'users.wonOver': '{count} ödemede {amount}',
  'users.section.behaviour': 'Davranış',
  'users.row.alarms': 'Alarmlar',
  'users.alarmsLive': '{total} alarmın {enabled} tanesi aktif',
  'users.row.challengesWon': 'Kazanılan görevler',
  'users.row.challengesMissed': 'Kaçırılan görevler',
  'users.row.wakeRate': 'Uyanma oranı',
  'users.nothingSettled': 'Henüz sonuçlanan yok',
  'users.section.device': 'Cihaz',
  'users.row.userId': 'Kullanıcı kimliği',
  'users.row.owner': 'Oturum açan hesap',
  'users.ownerNone': 'Oturum açılmadı',
  'users.ownerNameMissing': 'Ad paylaşılmadı',
  'users.row.deviceMoved': 'Bu telefona taşındı',
  'users.row.platform': 'Platform',
  'users.row.appVersion': 'Uygulama sürümü',
  'users.row.languageCountry': 'Dil / ülke',
  'users.row.attestation': 'Cihaz doğrulaması',
  'users.row.pushToken': 'Bildirim anahtarı',
  'users.pushPresent': 'Var',
  'users.pushNone': 'Yok — ulaşılamıyor',
  'users.row.lastIp': 'Son IP',
  'users.row.registered': 'Kayıt',
  'users.row.lastSeen': 'Son görülme',
  'users.section.subscription': 'Abonelik',
  'users.row.status': 'Durum',
  'users.subActive': 'Aktif',
  'users.row.expires': 'Bitiş',
  'users.grantSubscription': 'Abonelik ver',
  'users.cancelSubscription': 'İptal et',
  'users.tab.freeTrial': 'Ücretsiz deneme',
  'users.noTrialEvents': 'Bu hesap için ücretsiz deneme geçmişi yok.',
  'users.trialBy': '{actor} tarafından',
  'users.trialWindow': '{from} → {to}',
  'users.trialType.granted': 'Verildi',
  'users.trialType.denied': 'Reddedildi',
  'users.trialType.ended': 'Kullanıcı bitirdi',
  'users.trialType.expired': 'Süresi doldu',
  'users.trialType.converted': 'Ücretli plana dönüştü',
  'users.trialType.extended': 'Uzatıldı',
  'users.trialType.revoked': 'Geri alındı',
  'users.trialReason.disabled': 'Ücretsiz denemeler kapalıydı.',
  'users.trialReason.device_already_trialed':
    'Bu cihaz daha önce ücretsiz deneme aldı — büyük olasılıkla yeniden kurulum.',
  'users.trialReason.ip_limit_reached':
    'Politika penceresinde bu adresten çok fazla deneme verildi.',
  'users.trialReason.already_on_trial': 'Zaten bir deneme sürüyordu.',
  'users.trialReason.already_premium': 'Hesapta zaten Premium vardı.',
  'users.trialReason.account_inactive': 'Hesap devre dışı.',
  'users.section.freeTrial': 'Ücretsiz deneme',
  'users.trialActive': 'Sürüyor',
  'users.trialEnded': 'Bitti',
  'users.row.trialStarted': 'Başlangıç',
  'users.row.trialEnds': 'Bitiş',
  'users.grantFreeTrial': 'Ücretsiz deneme ver',
  'users.extendFreeTrial': 'Uzat',
  'users.revokeFreeTrial': 'Hemen bitir',
  'users.accessMode.subscription': 'Abonelik',
  'users.accessMode.free_trial': 'Ücretsiz deneme',
  'users.accessMode.reward': 'Ödül modu',
  'users.row.accessMode': 'Mod',
  'users.filter.isFreeTrial': 'Ücretsiz denemede',
  'users.dialog.grantTrial.title': 'Ücretsiz deneme ver',
  'users.dialog.grantTrial.description':
    'Şu andan itibaren ücretsiz Premium. Kayıtla aynı deneme günlüğüne yazılır; böylece huni sayılabilir kalır ve cihaz başına tek deneme kuralı çalışmaya devam eder.',
  'users.dialog.revokeTrial.title': 'Bu ücretsiz deneme bitirilsin mi?',
  'users.dialog.revokeTrial.description':
    'Premium hemen durur. Tarihler korunur, böylece geçmiş doğru okunmaya devam eder.',
  'users.section.account': 'Hesap',
  'users.deactivate': 'Devre dışı bırak',
  'users.reactivate': 'Yeniden etkinleştir',
  'users.deleteAccount': 'Hesabı sil',
  'users.days': 'Gün',
  'users.daysHint': 'Şu andan itibaren sayılır',

  'users.dialog.activate.title': 'Bu hesap yeniden etkinleştirilsin mi?',
  'users.dialog.activate.description':
    'Kullanıcı uygulamayı yeniden kullanabilecek.',
  'users.dialog.deactivate.title': 'Bu hesap devre dışı bırakılsın mı?',
  'users.dialog.deactivate.description':
    'Hesap çalışmayı durdurur ama geçmişini ve kredilerini korur. Kötüye kullanıma verilen olağan yanıt.',
  'users.dialog.delete.title': 'Bu hesap silinsin mi?',
  'users.dialog.delete.description':
    'Yumuşak silme — hesap ve kilitleri defterde kalır, ürettiği her olay saklanır.',
  'users.dialog.restore.title': 'Bu hesap geri getirilsin mi?',
  'users.dialog.restore.description':
    'Hesap ve elinde tuttuğu her şey geri gelir.',
  'users.dialog.subscribe.title': 'Abonelik ver',
  'users.dialog.subscribe.description':
    'Şu andan başlayan bir abonelik açar. Sınırsız alarmlar bunu gerektirir.',
  'users.dialog.unsubscribe.title': 'Bu abonelik iptal edilsin mi?',
  'users.dialog.unsubscribe.description':
    'Aboneliği kapatır. Geçmiş tarihler saklanır, çünkü destek ekibine bunlar sorulur.',

  'users.op.credit': 'Kredi ekle',
  'users.op.creditHint':
    'Bir yükleme, iyi niyet kredisi ya da yukarı yönlü düzeltme.',
  'users.op.debit': 'Kredi al',
  'users.op.debitHint': 'Harcanabilir bakiyeyi eksiye düşürmeyi reddeder.',
  'users.op.release': 'Kilidi çöz',
  'users.op.releaseHint': 'Kredileri tutup takılmış bir alarmın olağan çözümü.',
  'users.op.block': 'Kredi kilitle',
  'users.op.blockHint':
    'Nadiren istediğiniz şeydir — alarmlar kendi kilitlerini koyar.',
  'users.op.forfeit': 'Kilidi yak',
  'users.op.forfeitHint':
    'Kaçırılan bir görevin yaptığı gibi kredileri temelli alır.',
  'users.moveByHand': 'Krediyi elle taşı',
  'users.operation': 'İşlem',
  'users.amount': 'Tutar',
  'users.amountHint': 'Tam kredi',
  'users.ledger': 'Hareketler',
  'users.ledgerLine': '{reason} · {when} · bakiye {balance}',
  'users.nothingMoved': 'Hiçbir şey taşınmadı — o krediler orada değildi',
  'users.moved': '{amount} taşındı',
  'users.moveTheCredits': 'Krediyi taşı',
  'users.adjust': 'Düzelt',
  'users.viewAlarms': 'Alarmlar ({count})',
  'users.noAlarms': 'Bu hesabın aktif alarmı yok.',
  'users.alarmLine': '{schedule} · {won} kazanıldı / {missed} kaçırıldı',
  'users.openInAlarmList': 'Alarm listesinde aç',
  'users.noPayouts': 'Bu kullanıcıya hiçbir dağıtımdan pay verilmemiş.',
  'users.payoutLine':
    '{rank}. sırada · ağırlık {weight} · havuzun %{percent} kadarı · {when}',

  // ── Alarmlar ──────────────────────────────────────────────────────────────
  'alarms.title': 'Alarmlar',
  'alarms.pageTabsLabel': 'Alarm bölümleri',
  'alarms.tab.all': 'Tüm alarmlar',
  'alarms.scopedToUser': 'Tek bir hesabın alarmları gösteriliyor',
  'alarms.showEveryone': 'Herkesi göster',
  'alarms.description': 'Herkesin ne kurduğu ve ne kadar kilit tuttuğu',
  'alarms.filter.label': 'Etiket içerir',
  'alarms.filter.type': 'Tür',
  'alarms.filter.enabled': 'Etkin',
  'alarms.filter.show': 'Göster',
  'alarms.filter.live': 'Aktif alarmlar',
  'alarms.filter.holding': 'Yalnızca kredi kilitleyenler',
  'alarms.filter.deleted': 'Silinenleri de göster',
  'alarms.filter.sort': 'Sırala',
  'alarms.sort.default': 'En yeni alarm',
  'alarms.sort.defaultScoped': 'Çalma saati',
  'alarms.sort.lastEvent': 'Son olay',
  'alarms.type.reward': 'Ödüllü',
  'alarms.type.infinite': 'Sınırsız',
  'alarms.column.alarm': 'Alarm',
  'alarms.column.schedule': 'Program',
  'alarms.column.type': 'Tür',
  'alarms.column.challenge': 'Görev',
  'alarms.column.staked': 'Kilitli',
  'alarms.column.record': 'Kazanılan / kaçırılan',
  'alarms.column.lastRang': 'Son çalma',
  'alarms.column.lastEvent': 'Son olay',
  'alarms.noLabel': '(etiket yok)',
  'alarms.deletedBadge': 'Silindi',
  'alarms.holding':
    'Bu kullanıcının bakiyesinden {amount} tutuyor. Kapatmak ya da silmek bunu anında geri verir.',
  'alarms.section.schedule': 'Program',
  'alarms.row.ringsAt': 'Çalma saati',
  'alarms.row.repeats': 'Tekrar',
  'alarms.row.perWeek': 'Haftalık çalma',
  'alarms.row.tzOffset': 'Saat dilimi farkı',
  'alarms.notReported': 'Bildirilmedi',
  'alarms.minutesShort': '{count} dk',
  'alarms.row.enabled': 'Etkin',
  'alarms.section.challenge': 'Görev',
  'alarms.row.kind': 'Tür',
  'alarms.row.difficulty': 'Zorluk',
  'alarms.row.rounds': 'Tur sayısı',
  'alarms.row.timeLimit': 'Süre sınırı',
  'alarms.section.record': 'Geçmiş',
  'alarms.row.rang': 'Çaldı',
  'alarms.rangTimes': '{count} kez',
  'alarms.row.dismissed': 'Kapatıldı',
  'alarms.row.challengesWon': 'Kazanılan görevler',
  'alarms.row.challengesMissed': 'Kaçırılan görevler',
  'alarms.row.wakeRate': 'Uyanma oranı',
  'alarms.row.lifetimeCost': 'Toplam maliyet',
  'alarms.row.lastRang': 'Son çalma',
  'alarms.row.lastDismissed': 'Son kapatma',
  'alarms.row.lastEvent': 'Son olay',
  'alarms.section.identity': 'Kimlik',
  'alarms.row.alarmId': 'Alarm kimliği',
  'alarms.row.user': 'Kullanıcı',
  'alarms.row.device': 'Cihaz',
  'alarms.row.clientId': 'Cihazdaki kimlik',
  'alarms.row.created': 'Oluşturma',
  'alarms.changeTime': 'Saati değiştir',
  'alarms.hour': 'Saat',
  'alarms.minute': 'Dakika',
  'alarms.label': 'Etiket',
  'alarms.editTimeAndLabel': 'Saati ve etiketi düzenle',
  'alarms.switchOff': 'Kapat',
  'alarms.switchOn': 'Aç',
  'alarms.dialog.delete.title': 'Bu alarm silinsin mi?',
  'alarms.dialog.restore.title': 'Bu alarm geri getirilsin mi?',
  'alarms.dialog.save.title': 'Bu değişiklikler kaydedilsin mi?',
  'alarms.dialog.off.title': 'Bu alarm kapatılsın mı?',
  'alarms.dialog.on.title': 'Bu alarm açılsın mı?',
  'alarms.dialog.delete.description':
    'Yumuşak silme. Tuttuğu kilit anında kullanıcıya geri döner.',
  'alarms.dialog.other.description':
    'Kilit, alarmın şu anda ihtiyaç duyduğu tutara göre güncellenir.',
  'alarms.drawerFallback': 'Alarm',
  'alarms.tabsLabel': 'Alarm bölümleri',
  'alarms.tab.detail': 'Ayrıntılar',
  'alarms.tab.events': 'Olaylar',
  'alarms.viewUser': 'Kullanıcıyı aç',

  // ── Olaylar ───────────────────────────────────────────────────────────────
  'events.tab.review': 'İnceleme kuyruğu',
  'events.viewAlarm': 'Alarmı aç',
  'events.viewUser': 'Kullanıcıyı aç',
  'events.viewEvent': 'Olay ayrıntısı',
  'events.noneForAlarm': 'Henüz kayıt yok',
  'events.noneForAlarmDescription': 'Bu alarm bu türde bir şey bildirmemiş.',
  'events.section.belongsTo': 'Bağlı olduğu alarm',
  'events.section.what': 'Ne oldu',
  'events.section.metadata': 'Bildirilen her şey',
  'events.noAlarm': 'Bu olay bir alarma bağlı değil.',
  'events.row.occurred': 'Gerçekleşme',
  'events.row.received': 'Bildirilme',
  'events.row.eventId': 'Olay kimliği',
  'events.filter.kind': 'Tür',
  'events.filter.risk': 'Kuralların değerlendirmesi',
  'events.type.all': 'Hepsi',
  'events.type.rang': 'Alarm çaldı',
  'events.type.dismissed': 'Kapatıldı',
  'events.type.reward_earned': 'Görev kazanıldı',
  'events.type.reward_missed': 'Görev kaçırıldı',
  'events.type.reward_withheld': 'Talep beklemede',
  'events.type.ring_timed_out': 'Çözülmeden sustu',
  'events.type.created': 'Alarm oluşturuldu',
  'events.type.deleted': 'Alarm silindi',
  'events.type.rejected': 'Kayıt reddedildi',
  'events.type.integrity_failed': 'Cihaz doğrulaması başarısız',
  'events.risk.trusted': 'Güvenilir',
  'events.risk.suspicious': 'Şüpheli',
  'events.risk.rejected': 'Reddedildi',
  'events.column.what': 'Ne oldu',
  'events.column.credits': 'Kredi',
  'events.detail.solvedIn': '{duration} içinde çözüldü',
  'events.detail.ruledBy': '{actor} karar verdi',
  'events.detail.alarm': 'alarm #{id}',
  'events.review.title': 'Karar bekleyen talepler',
  'events.review.description':
    'Biri karar verene kadar kilitleri tutulur. Onaylamak ödülü verir; reddetmek kilidi yakar.',
  'events.review.emptyTitle': 'İncelenecek bir şey yok',
  'events.review.emptyDescription':
    'Şimdiye kadarki her ödül talebi kurallar tarafından kendiliğinden sonuçlandırıldı.',
  'events.review.approve': 'Onayla',
  'events.review.reject': 'Reddet',
  'events.review.ruled': 'Karara bağlandı — karar olay kaydında',
  'events.review.approveTitle': 'Bu ödül verilsin mi?',
  'events.review.rejectTitle': 'Bu talep reddedilip kilit yakılsın mı?',
  'events.review.approveDescription':
    'Ödül verilir ve kilit olduğu yerde kalır, bir sonraki çalmaya hazır.',
  'events.review.rejectDescription':
    'Kilit, kaçırılmış bir görevin alacağı gibi alınır.',
  'events.review.approveConfirm': 'Ödülü ver',
  'events.review.rejectConfirm': 'Kilidi yak',
  'events.review.view.pending': 'Bekleyen',
  'events.review.view.history': 'Geçmiş',
  'events.review.historyTitle': 'Karara bağlanan talepler',
  'events.review.historyDescription':
    'Burada verilen her karar, en yenisi önce; kimin verdiği ve nedeniyle.',
  'events.review.historyEmptyTitle': 'Henüz karar yok',
  'events.review.historyEmptyDescription':
    'Kuyruktan onaylanan veya reddedilen talepler burada görünür.',
  'events.review.approved': 'Onaylandı',
  'events.review.rejected': 'Reddedildi',
  'events.review.forfeited': '{amount} kredi yakıldı',
  'events.review.viewClaim': 'İlk talep',

  // ── Offline rewards ──────────────────────────────────────────────────────
  'offline.tab': 'Çevrimdışı ödüller',
  'offline.stat.completions': 'Çevrimdışı kazanılan',
  'offline.stat.window': 'Son {days} gün',
  'offline.stat.users': 'Hesap',
  'offline.stat.allowance': '{days} günde {allowance} ödenir, sonrası bekletilir',
  'offline.stat.repeatUsers': 'Sınırı aşan',
  'offline.stat.repeatCaption': 'Tekrarlayan örüntü — devre dışı bırakmayı düşünün',
  'offline.stat.outcomes': 'Ödenen',
  'offline.stat.outcomesCaption': '{withheld} incelemede · {lost} kaybedildi',
  'offline.users.title': 'İnternetsiz kazanan hesaplar',
  'offline.users.description':
    'İnternet yokken tarama yerine daha zor bir matematik görevi verilir. {days} gün içindeki ilk {allowance} kazanç ödenir ve işaretlenir; sonrakiler inceleme kuyruğunda bekler. Devre dışı bırakmak için hesabı açın.',
  'offline.users.emptyTitle': 'Çevrimdışı ödül yok',
  'offline.users.emptyDescription':
    'Son {days} günde kimse internetsiz ödül kazanmadı.',
  'offline.column.account': 'Hesap',
  'offline.column.completions': 'Çevrimdışı kazanç',
  'offline.column.outcomes': 'Ödenen · bekleyen · kaybedilen',
  'offline.column.last': 'En son',
  'offline.outcomes': '{earned} · {withheld} · {lost}',
  'offline.overAllowance': 'Sınırı aştı',
  'offline.deactivated': 'Devre dışı',
  'offline.showEvents': 'Olaylar',
  'offline.events.title': 'Son çevrimdışı talepler',
  'offline.events.description':
    'Her talep ve sunucunun kararı. Bekleyen talepler inceleme sekmesinden karara bağlanır.',
  'offline.events.focused': 'Tek hesap gösteriliyor:',
  'offline.events.showAll': 'Herkesi göster',
  'offline.events.emptyTitle': 'Çevrimdışı talep yok',
  'offline.decision.earned': 'Ödendi',
  'offline.decision.withheld': 'İncelemede',
  'offline.decision.missed': 'Kaybedildi — geç kalındı',
  'offline.decision.refused': 'Reddedildi — alarm zaten sonuçlanmıştı',

  // ── Ödüller ───────────────────────────────────────────────────────────────
  'economy.title': 'Ödüller',
  'economy.description':
    'Kaçırılan sabahlar başkalarının kredisine nasıl dönüşür',
  'economy.tabsLabel': 'Ödül bölümleri',
  'economy.tab.runs': 'Dağıtımlar',
  'economy.tab.schemes': 'Şemalar',

  'economy.runs.draftTitle': 'Bir geceyi sonuçlandır',
  'economy.runs.draftDescription':
    'Her payı hesaplar, hiçbir şeyi taşımaz. Baktıktan sonra siz onaylarsınız.',
  'economy.runs.draftAction': 'Dünü taslakla',
  'economy.runs.drafted':
    'Taslak hazır: {participants} kişiden {winners} tanesi kalktı, havuz {pool}',
  'economy.runs.emptyTitle': 'Henüz dağıtım yok',
  'economy.runs.emptyDescription':
    'Hiçbir kredi hareket etmeden kimin ne alacağını görmek için bir taslak oluşturun.',
  'economy.runs.confirmTitle': 'Dün için bir dağıtım taslağı hazırlansın mı?',
  'economy.runs.confirmDescription':
    'Hiçbir şey hareket etmez. Her pay hesaplanır ve bakabileceğiniz, onaylayabileceğiniz ya da atabileceğiniz bir taslak olarak yazılır.',
  'economy.runs.confirmAction': 'Taslağı oluştur',
  'economy.column.night': 'Gece',
  'economy.column.wokeUp': 'Kalkanlar',
  'economy.column.pool': 'Havuz',
  'economy.column.commission': 'Komisyon',
  'economy.column.paid': 'Dağıtılan',
  'economy.column.startedBy': 'Başlatan',

  'economy.run.title': 'Dağıtım · {when}',
  'economy.run.discard': 'At',
  'economy.run.payOut': 'Ödemeyi yap',
  'economy.run.draftNote':
    'Henüz hiçbir şey hareket etmedi. Bunlar ödenecek olan paylar.',
  'economy.run.paid': '{amount} ödendi',
  'economy.run.cancelled': 'Dağıtım iptal edildi',
  'economy.run.section.night': 'Gece',
  'economy.run.row.window': 'Aralık',
  'economy.run.row.settled': 'Sonuçlanan çalmalar',
  'economy.run.row.wokeUp': 'Kalkanlar',
  'economy.run.row.missed': 'Kaçıranlar',
  'economy.run.row.withheld': 'İnceleme bekleyenler',
  'economy.run.section.books': 'Defter',
  'economy.run.row.carriedIn': 'Önceki dönemden devir',
  'economy.run.row.pool': 'Havuz',
  'economy.run.row.commission': 'Komisyon',
  'economy.run.row.paidToUsers': 'Kullanıcılara ödenen',
  'economy.run.row.carriedForward': 'Sonraki döneme devir',
  'economy.run.section.schemes': 'Her şemanın yaptığı',
  'economy.run.noSchemes': 'Etkin bir şema yoktu.',
  'economy.run.fromPool': 'Havuzdan',
  'economy.run.minted': 'Basıldı',
  'economy.run.schemeApplied':
    '{recipients} alıcı · {pool} tutarındaki potadan {paid} ödendi',
  'economy.run.schemeCommission': '{amount} komisyon',
  'economy.run.schemeSkipped': 'Uygulanmadı — {reason}',
  'economy.run.section.allocations': 'Kim ne alıyor',
  'economy.run.noAllocations': 'Kimseye pay verilmedi.',
  'economy.run.allocationLine': '{scheme} · ağırlık {weight} · %{percent}',
  'economy.run.allocationSolved': '{duration} içinde çözdü',
  'economy.run.allocationStreak': '{count} günlük seri',
  'economy.run.commitTitle': '{amount} ödensin mi?',
  'economy.run.commitDescription':
    'Her pay tek tek işlenir ve ulaştıkça ödendi olarak işaretlenir. Bu işlem buradan geri alınamaz.',
  'economy.run.cancelTitle': 'Bu taslak atılsın mı?',
  'economy.run.cancelDescription':
    'Taslak, bir dağıtımın hesaplanıp bilerek ödenmediğinin kaydı olarak saklanır.',

  'economy.schemes.title': 'Bir gece nasıl paylaşılır',
  'economy.schemes.description':
    'Şemalar öncelik sırasıyla çalışır ve her biri önceki şemaların ne harcadığını görür.',
  'economy.schemes.new': 'Yeni şema',
  'economy.schemes.emptyTitle': 'Henüz şema yok',
  'economy.schemes.emptyDescription':
    'Şema olmadan bir dağıtım yakılanları toplar ama kimseye ödeme yapmaz. Eşit paylaşımla başlayın.',
  'economy.schemes.emptyAction': 'İlk şemayı oluştur',
  'economy.schemes.mintedBonus': 'Basılan bonus',
  'economy.schemes.commissionPercent': '%{percent} komisyon',
  'economy.schemes.splitsBy': 'Paylaşım ölçütü',
  'economy.schemes.appliesWhen': 'Ne zaman uygulanır',

  'economy.basis.equal': 'Eşit paylaşım',
  'economy.basis.speed': 'Ne kadar hızlı çözdükleri',
  'economy.basis.streak': 'Serilerinin uzunluğu',
  'economy.basis.stake': 'Kilitledikleri tutar',
  'economy.basis.rank': 'Bitiriş sırası (kademeli)',
  'economy.basis.custom': 'Bir formül',

  'economy.editor.newTitle': 'Yeni ödül şeması',
  'economy.editor.editTitle': '“{name}” düzenleniyor',
  'economy.editor.create': 'Şema oluştur',
  'economy.editor.namePlaceholder': 'örn. Sabah paylaşımı',
  'economy.editor.descriptionHint': 'Bu şema ne için',
  'economy.editor.enabledYes': 'Evet — her dağıtımda çalışır',
  'economy.editor.enabledNo': 'Hayır — saklanır ama hiç uygulanmaz',
  'economy.editor.priorityHint':
    'Düşük olan önce çalışır ve havuzun tamamını görür',
  'economy.editor.section.source': 'Krediler nereden geliyor',
  'economy.editor.source': 'Kaynak',
  'economy.editor.sourcePoolHint':
    'Alarmını kaçıranların kaybettikleri. İşletmeye hiçbir maliyeti yok.',
  'economy.editor.sourceSystemHint':
    'İşletmenin bastığı krediler. Bunun bir tavanı olmak zorunda.',
  'economy.editor.sourcePool': 'Bu gecenin yakılanları',
  'economy.editor.sourceSystem': 'İşletmenin ödediği bir bonus',
  'economy.editor.poolShare': 'Havuz payı (%)',
  'economy.editor.poolShareHint':
    'Bir geceyi birkaç şemanın paylaşması için düşürün',
  'economy.editor.commission': 'Komisyon (%)',
  'economy.editor.commissionHint': 'Kasanın önce alınan payı',
  'economy.editor.perWinner': 'Kazanan başına kredi',
  'economy.editor.perWinnerHint':
    'Bunun yerine sabit bir bütçeyi paylaştırmak için 0 bırakın',
  'economy.editor.budget': 'Bütçe tavanı',
  'economy.editor.budgetHint': 'Tek çalıştırmada basabileceği en yüksek tutar',
  'economy.editor.section.split': 'Nasıl paylaşılır',
  'economy.editor.basis': 'Ölçüt',
  'economy.editor.weightPerWinner': 'Kazanan başına ağırlık',
  'economy.editor.minPayout': 'Yapmaya değer en küçük ödeme',
  'economy.editor.minPayoutHint': 'Bunun altındaki paylar ödenmez, düşürülür',
  'economy.editor.maxShare': 'Tek pay üst sınırı (%)',
  'economy.editor.remainder': 'Artan krediler',
  'economy.editor.remainderHint':
    'Tam sayı paylar bir potayı neredeyse hiç eşit bölmez — artanın nereye gideceğini bu belirler',
  'economy.editor.remainderLargest':
    'Yuvarlamada en çok kaybeden paylara verilsin',
  'economy.editor.remainderHouse': 'Komisyon olarak kalsın',
  'economy.editor.remainderCarry': 'Sonraki geceye devredilsin',
  'economy.editor.section.applies': 'Ne zaman uygulanır',
  'economy.editor.cycleCondition': 'Yalnızca şu gecelerde…',
  'economy.editor.cyclePlaceholder':
    'Her zaman uygulanması için boş bırakın. örn. missed == 0',
  'economy.editor.participantCondition': 'Yalnızca şu kazananlar için…',
  'economy.editor.participantPlaceholder':
    'Her kazanan için boş bırakın. örn. streak >= 3',
  'economy.editor.section.eligible': 'Kimler uygun',
  'economy.editor.eligibleNote':
    'Kullanıcı listesi ve bildirim durumlarıyla aynı dilde daha da daraltır. Kalkan herkes için boş bırakın.',
  'economy.editor.updated': 'Şema güncellendi',
  'economy.editor.created': 'Şema oluşturuldu',
  'economy.editor.deleted': 'Şema silindi — bir daha çalışmayacak',
  'economy.editor.deleteTitle': '“{name}” silinsin mi?',
  'economy.editor.createTitle': 'Bu şema oluşturulsun mu?',
  'economy.editor.deleteDescription':
    'Gelecekteki dağıtımlara uygulanmayı bırakır. Daha önce ürettiği paylar saklanır.',
  'economy.editor.saveDescription':
    'Bir sonraki dağıtımdan itibaren geçerli olur. Mevcut çalıştırmalara dokunulmaz.',

  'economy.formula.placeholder': 'örn. speedScore * 3 + streak + 1',
  'economy.formula.valid': 'Geçerli — örnek değerlerle sonuç {result}',
  'economy.formula.variables': 'Değişkenler ve örnekler',

  // ── Bildirimler ───────────────────────────────────────────────────────────
  'notif.title': 'Bildirimler',
  'notif.description':
    'Şimdi bir mesaj gönderin ya da kendiliğinden gidenleri kurun',
  'notif.tabsLabel': 'Bildirim bölümleri',
  'notif.tab.compose': 'Yeni mesaj',
  'notif.tab.cases': 'Durumlar',
  'notif.tab.templates': 'Şablonlar',
  'notif.tab.history': 'Geçmiş',
  'notif.tab.blacklist': 'Susturulanlar',
  'notif.queue.overdue': 'zamanı geçmiş {count} bildirim',
  'notif.queue.scheduled': '{count} planlandı',
  'notif.queue.nextAt': 'sıradaki {time}',

  'notif.compose.step1': 'Mesajı yazın',
  'notif.compose.step2': 'Kimin alacağını seçin',
  'notif.compose.step3': 'Deneyin, sonra gönderin',
  'notif.compose.source': 'Metin nereden geliyor',
  'notif.compose.sourceAdhoc': 'Buraya yazın — tek seferlik',
  'notif.compose.sourceTemplate':
    'Kayıtlı bir şablon kullanın — çevrilmiş ve yeniden kullanılabilir',
  'notif.compose.titleHint': 'Kısa tutun — kilit ekranı metni keser',
  'notif.compose.titlePlaceholder': 'Günaydın',
  'notif.compose.bodyHint':
    'Tek seferlik metin yazıldığı gibi, tek dilde gider',
  'notif.compose.bodyPlaceholder':
    'Alarmınız 07:00 için kurulu. Görevi geçin ve kilidinizi koruyun.',
  'notif.compose.namedUsers': 'Belirli cihazlar',
  'notif.compose.namedUsersHint':
    'Cihaz kimliklerini virgül veya boşlukla ayırarak yapıştırın. Aşağıdaki koşulları kullanmak için boş bırakın.',
  'notif.compose.willReceive': 'Bunu {count} kullanıcı alacak',
  'notif.compose.silencedSkipped': '(susturulan kullanıcılar atlanır)',
  'notif.compose.testOn': 'Tek cihazda dene',
  'notif.compose.testHint': 'Yalnızca o cihaza gider',
  'notif.compose.sendTest': 'Deneme gönder',
  'notif.compose.backgroundNote':
    'Gönderim arka planda çalışır; ilerlemesi Geçmiş altında görünür.',
  'notif.compose.send': 'Bildirimi gönder',
  'notif.compose.confirmTitle': '{count} kullanıcıya gönderilsin mi?',
  'notif.compose.confirmDescription': 'Bu anında gider ve geri çağrılamaz.',
  'notif.compose.confirmAction': 'Gönder',
  'notif.compose.sent':
    '{count} kullanıcıya gönderiliyor — Geçmiş altından takip edin',
  'notif.compose.testSent': 'Deneme bildirimi gönderildi',
  'notif.compose.testSkipped':
    'Atlandı — o kullanıcının kullanılabilir bir bildirim anahtarı yok',
  'notif.compose.testRejected': 'Bildirim sağlayıcısı reddetti',
  'notif.compose.languages': '{count} dil',
  'notif.compose.previewNote':
    'Her kullanıcı bunu çevirisi varsa kendi dilinde, yoksa İngilizce alır.',
  'notif.compose.previewPlaceholders':
    'Alıcıya göre doldurulan yer tutucular: {list}.',

  'notif.cases.title': 'Bu olduğunda şunu gönder',
  'notif.cases.description':
    'Koşullar durum tetiklendiğinde değil, gönderim anında yeniden kontrol edilir — böylece bekleme sırasında sorunu çözen kullanıcı sessizce listeden düşer.',
  'notif.cases.new': 'Yeni durum',
  'notif.cases.emptyTitle': 'Henüz durum yok',
  'notif.cases.emptyDescription':
    'İyi bir başlangıç: kayıttan yarım saat sonra hâlâ alarmı yoksa kullanıcıyı karşılayın.',
  'notif.cases.emptyAction': 'İlk durumu oluştur',
  'notif.cases.swept': 'Taramalı',
  'notif.cases.recurring': 'Zamanlanmış',
  'notif.cases.nextRun': 'sonraki {time}',
  'notif.cases.lastRun': 'son çalışma {time}',
  'notif.cases.everyDay': 'Her gün',
  'notif.cases.weekdays': 'Hafta içi',
  'notif.cases.weekends': 'Hafta sonu',
  'notif.cases.at': 'saat',
  'notif.cases.section.schedule': 'Zamanlama',
  'notif.cases.scheduleTime': 'Saat',
  'notif.cases.scheduleZone': 'Saat dilimi',
  'notif.cases.scheduleDays': 'Günler',
  'notif.cases.scheduleNote':
    'Seçilen günlerde bu saatte bir kez çalışır ve o an aşağıdaki koşullara uyan herkese ulaşır. Herkese ulaşmak için koşulları boş bırakın.',
  'notif.cases.scheduleDaysRequired': 'En az bir gün seçin',
  'notif.cases.day.1': 'Pzt',
  'notif.cases.day.2': 'Sal',
  'notif.cases.day.3': 'Çar',
  'notif.cases.day.4': 'Per',
  'notif.cases.day.5': 'Cum',
  'notif.cases.day.6': 'Cmt',
  'notif.cases.day.7': 'Paz',
  'notif.cases.event': 'Olay',
  'notif.cases.queuedSoFar': 'şimdiye dek {count} kuyruğa alındı',
  'notif.cases.wait': 'bekle',
  'notif.cases.send': 'gönder',
  'notif.cases.deletedTemplate': 'silinmiş bir şablon',
  'notif.cases.cooldown': 'en fazla {hours} saatte bir',
  'notif.cases.delayMinutes': '{count} dk',
  'notif.cases.delayHours': '{count} sa',
  'notif.cases.delayDays': '{count} gün',
  'notif.cases.editTitle': '“{name}” düzenleniyor',
  'notif.cases.newTitle': 'Yeni durum',
  'notif.cases.create': 'Durum oluştur',
  'notif.cases.namePlaceholder': 'örn. Kaçan sabah sonrası dürtme',
  'notif.cases.enabledNo':
    'Hayır — yeni kayıt almayı durdurur ve kuyruğa alınanları iptal eder',
  'notif.cases.section.fires': 'Ne zaman tetiklenir',
  'notif.cases.trigger': 'Tetikleyici',
  'notif.cases.sweptWarning':
    'Taramalı bir durum bir olayla değil, düzenli aralıklarla değerlendirilir; bu yüzden koşulları olmak zorunda ve bir bekleme süresi olmalı — yoksa sürekli doğru kalan bir koşul her turda aynı kişilere bildirim gönderir.',
  'notif.cases.delay': 'Göndermeden önce bekle (dakika)',
  'notif.cases.delayHint': '0 ise bir sonraki turda gider',
  'notif.cases.expires': 'Şu süre sonra vazgeç (dakika)',
  'notif.cases.expiresHint':
    'Birikmiş kuyruğun bayat bildirim göndermesini önler. Boşsa hiç sona ermez.',
  'notif.cases.section.sends': 'Ne gönderir',
  'notif.cases.fillsAlso': 'Bu tetikleyici şunları da doldurur:',
  'notif.cases.section.conditions': 'Yalnızca hâlâ böyleyseler',
  'notif.cases.conditionsNote':
    'Durum tetiklendiğinde değil, gönderim anında canlı hesaba karşı kontrol edilir. Beklemeyi işe yarar kılan da budur: bu arada sorunu çözen kişi sessizce çıkarılır.',
  'notif.cases.previewMatch': 'Şu anda bu koşullara {count} kullanıcı uyuyor',
  'notif.cases.previewCaveat':
    ' — bu bir tahmin değil, akıl sağlığı kontrolü: olay tabanlı bir durum yalnızca kendisi var olduktan sonra tetikleyen kullanıcılara ulaşır',
  'notif.cases.section.limits': 'Sınırlar',
  'notif.cases.cooldownLabel': 'En fazla şu sürede bir (saat)',
  'notif.cases.cooldownHint':
    'Çoğunlukla koşulu doğru kalabilen taramalı durumlar için',
  'notif.cases.maxPerRun': 'Tarama başına üst sınır',
  'notif.cases.maxPerRunHint':
    'Taramalı bir durum tüm kitleye aynı anda ulaşamasın diye',
  'notif.cases.updated': 'Durum güncellendi',
  'notif.cases.created': 'Durum oluşturuldu',
  'notif.cases.deleted': 'Durum silindi — kuyruğa aldığı her şey iptal edildi',
  'notif.cases.deleteTitle': '“{name}” silinsin mi?',
  'notif.cases.createTitle': 'Bu durum oluşturulsun mu?',
  'notif.cases.deleteDescription':
    'Kuyruğa aldığı her şey de iptal edilir, böylece bundan sonra hiçbir şey gitmez.',
  'notif.cases.saveDescription':
    'Bundan sonra tetiklenen olaylar için geçerli olur.',

  'notif.templates.title': 'Kayıtlı metinler',
  'notif.templates.description':
    'Bir kez yazılır, her dile çevrilir ve onu gönderen her durum tarafından yeniden kullanılır.',
  'notif.templates.new': 'Yeni şablon',
  'notif.templates.emptyTitle': 'Henüz şablon yok',
  'notif.templates.emptyDescription':
    'Durumların birine ihtiyacı var. Tek seferlik bir mesajın yok — onu doğrudan Yeni mesaj altında yazın.',
  'notif.templates.emptyAction': 'İlk şablonu oluştur',
  'notif.templates.usedBy': '{count} durum',
  'notif.templates.editTitle': '“{name}” düzenleniyor',
  'notif.templates.newTitle': 'Yeni şablon',
  'notif.templates.create': 'Şablon oluştur',
  'notif.templates.nameHint': 'Yöneticinin seçerken göreceği ad',
  'notif.templates.namePlaceholder': 'örn. Kaçan sabah dürtmesi',
  'notif.templates.key': 'Anahtar',
  'notif.templates.keyHint':
    'Sabit makine adı. Küçük harf, rakam, nokta ve tire.',
  'notif.templates.section.copy': 'Metin',
  'notif.templates.addLanguage': 'Dil ekle',
  'notif.templates.englishRequired':
    'İngilizce zorunlu — çevirisi olmayan her cihazın aldığı yedek metin odur.',
  'notif.templates.placeholderLead':
    'Metinlerde şu tür yer tutucular kullanılabilir:',
  'notif.templates.placeholderTail':
    'Gönderim anında her alıcı için doldurulur.',
  'notif.templates.updated': 'Şablon güncellendi',
  'notif.templates.created': 'Şablon oluşturuldu',
  'notif.templates.deleted': 'Şablon silindi',
  'notif.templates.deleteTitle': '“{name}” silinsin mi?',
  'notif.templates.createTitle': 'Bu şablon oluşturulsun mu?',
  'notif.templates.deleteDescription':
    'Herhangi bir durum bunu hâlâ gönderiyorsa reddedilir — önce onları başka şablona yönlendirin ya da kapatın.',
  'notif.templates.saveDescription':
    'Bu şablonu gönderen durumlar yeni metni anında kullanmaya başlar.',

  'notif.history.tabsLabel': 'Geçmiş görünümleri',
  'notif.history.tab.campaigns': 'Gönderimler',
  'notif.history.tab.jobs': 'Planlananlar',
  'notif.history.campaignsTitle': 'Gönderimler',
  'notif.history.campaignsDescription':
    'Toplu ve doğrudan mesajlar, en yeniden başlayarak',
  'notif.history.column.message': 'Mesaj',
  'notif.history.column.delivered': 'İletilen',
  'notif.history.column.skipped': 'Atlanan',
  'notif.history.column.failed': 'Başarısız',
  'notif.history.column.sentBy': 'Gönderen',
  'notif.history.column.when': 'Ne zaman',
  'notif.history.fromTemplate': 'Bir şablondan',
  'notif.history.emptyTitle': 'Henüz bir şey gönderilmedi',
  'notif.history.emptyDescription':
    'Yeni mesaj bölümünden gönderdikleriniz iletim sayılarıyla birlikte burada görünür.',
  'notif.history.column.outcome': 'Sonuç',
  'notif.history.column.device': 'Cihaz',
  'notif.history.column.why': 'Neden',
  'notif.history.column.due': 'Zamanı',
  'notif.history.column.settled': 'Sonuçlanma',
  'notif.history.attempts': '{count} deneme',
  'notif.history.outcome': 'Sonuç',
  'notif.history.status.pending': 'Bekliyor',
  'notif.history.status.sent': 'Gönderildi',
  'notif.history.status.skipped': 'Atlandı',
  'notif.history.status.failed': 'Başarısız',
  'notif.history.status.cancelled': 'İptal edildi',
  'notif.history.jobsTitle': 'Planlanan gönderimler',
  'notif.history.jobsDescription':
    'Bir durumun kuyruğa aldığı her iş ve zamanı geldiğinde işçinin verdiği karar',
  'notif.history.jobsEmptyTitle': 'Planlanmış bir şey yok',
  'notif.history.jobsEmptyDescription':
    'Durumlar, tetikleyicileri çalıştıkça gönderimlerini buraya kuyruklar.',

  'notif.blacklist.title': 'Susturulan kullanıcılar',
  'notif.blacklist.description':
    'Hiçbir şey almazlar — ne toplu mesaj, ne durum bildirimi, ne de deneme.',
  'notif.blacklist.add': 'Bir kullanıcıyı sustur',
  'notif.blacklist.column.device': 'Cihaz',
  'notif.blacklist.column.user': 'Kullanıcı',
  'notif.blacklist.column.why': 'Neden',
  'notif.blacklist.column.addedBy': 'Ekleyen',
  'notif.blacklist.unsilence': 'Susturmayı kaldır',
  'notif.blacklist.emptyTitle': 'Susturulan kimse yok',
  'notif.blacklist.emptyDescription':
    'Bildirim almayı bırakmak isteyen birini buraya ekleyin.',
  'notif.blacklist.added': 'Susturuldu — hiçbir bildirim almayacak',
  'notif.blacklist.removed': 'Yeniden bildirim alabilir',
  'notif.blacklist.addTitle': 'Bu kullanıcı susturulsun mu?',
  'notif.blacklist.addDescription':
    'Bu listeden çıkarılana kadar hiçbir yoldan bildirim almayacak.',
  'notif.blacklist.addAction': 'Sustur',
  'notif.blacklist.removeTitle': 'Bu kullanıcı yeniden bildirim alsın mı?',
  'notif.blacklist.removeDescription':
    'Susturulduğuna dair kayıt işlem kaydında kalır.',

  // ── İşlem kaydı ───────────────────────────────────────────────────────────
  'conversions.title': 'Mod geçişleri',
  'conversions.description':
    'Ödül modu ile Premium arasındaki her geçiş: kullanıcıya gösterilen teklif, neyin aktarıldığı ve nasıl sonuçlandığı.',
  'conversions.window': 'Özet aralığı',
  'conversions.lastDays': 'Son {count} gün',
  'conversions.filter.direction': 'Yön',
  'conversions.filter.anyDirection': 'Her iki yön',
  'conversions.filter.status': 'Durum',
  'conversions.filter.anyStatus': 'Tüm durumlar',
  'conversions.direction.reward_to_premium': 'Ödül → Premium',
  'conversions.direction.premium_to_reward': 'Premium → Ödül',
  'conversions.status.pending': 'Sürüyor',
  'conversions.status.completed': 'Tamamlandı',
  'conversions.status.failed': 'Başarısız',
  'conversions.status.expired': 'Yarıda bırakıldı',
  'conversions.tile.toPremium': "Premium'a geçenler",
  'conversions.tile.spent': '{credits} harcandı',
  'conversions.tile.toReward': 'Ödül moduna geçenler',
  'conversions.tile.granted': '{credits} verildi',
  'conversions.tile.applied': 'Uygulanan Premium kredisi',
  'conversions.tile.appliedCaption': 'Fiyattan düşülen kullanılmamış Premium',
  'conversions.tile.collected': 'Kartla tahsil edilen',
  'conversions.tile.collectedCaption': 'Geçişlerde kartla ödenen, iki yönde',
  'conversions.tile.forfeited': 'Yanan kullanılmamış değer',
  'conversions.tile.forfeitedCaption': 'Paket fiyatını aşan kredi',
  'conversions.tile.stalled': 'Başarısız veya yarım kalan',
  'conversions.tile.pending': '{count} tanesi sürüyor',
  'conversions.row.toPremium': '{plan} için {credits}',
  'conversions.row.toPremiumMixed': '{plan} için {credits} + kartla {paid}',
  'conversions.row.toReward': '{package}, kartla {paid} ödendi',
  'conversions.badge.partCard': 'Kısmen kart',
  'conversions.badge.unpaidPremium': 'Ödenmemiş Premium — kredi yok',
  'conversions.lateSettlement': 'Süre dolduktan sonra ödendi',
  'conversions.openUser': 'Kullanıcı',
  'conversions.showDetails': 'Ayrıntılar',
  'conversions.hideDetails': 'Gizle',
  'conversions.emptyTitle': 'Mod geçişi yok',
  'conversions.emptyDescription':
    'Bu filtrelere uyan kayıt yok. Bir kullanıcı geçiş başlattığı anda burada görünür.',
  'conversions.detail.item': 'Satın alınan',
  'conversions.detail.price': 'Kart fiyatı',
  'conversions.detail.creditsSpent': 'Harcanan gün ışığı',
  'conversions.detail.periodValue': 'Dönemin kayıtlı değeri',
  'conversions.detail.premiumEnds': 'Premium bitişi',
  'conversions.detail.remaining': 'Kalan Premium süresi',
  'conversions.detail.remainingValue': 'Bu sürenin değeri',
  'conversions.detail.applied': 'Uygulanan kredi',
  'conversions.detail.forfeited': 'Yanan kredi',
  'conversions.detail.charged': 'Karttan çekilen',
  'conversions.detail.creditsGranted': 'Verilen gün ışığı',
  'conversions.detail.balanceBefore': 'Önceki cüzdan',
  'conversions.detail.balanceValue': 'Toplam {balance} · harcanabilir {available}',
  'conversions.detail.conversionId': 'Geçiş',
  'conversions.detail.userId': 'Kullanıcı',
  'conversions.detail.subscription': 'Abonelik',
  'conversions.detail.period': 'Devredilen dönem',
  'conversions.detail.payment': 'Ödeme',
  'conversions.detail.clientIp': 'IP adresi',
  'conversions.detail.created': 'Başladı',
  'conversions.detail.finished': 'Bitti',
  'conversions.detail.planCredits': 'Planın gün ışığı fiyatı',
  'conversions.proof.title': 'Ödeme kontrolü',
  'conversions.proof.legacy':
    'Kaydedilmemiş — bu geçiş ödeme kontrolü başlamadan önce yapıldı.',
  'conversions.proof.status': 'Premium ödenmiş mi',
  'conversions.proof.verified': 'Doğrulandı',
  'conversions.proof.unverified': 'Ödenmemiş',
  'conversions.proof.funding': 'Ödeme şekli',
  'conversions.proof.funding.card': 'Kart',
  'conversions.proof.funding.daylights': 'Gün ışığı',
  'conversions.proof.paid': 'Dönem için tahsil edilen',
  'conversions.proof.recorded': 'Dönemin kayıtlı değeri',
  'conversions.proof.evidence': 'Kanıt',
  'conversions.proof.reason': 'Kredi verilmeme nedeni',
  'conversions.proof.reason.NO_SUBSCRIPTION':
    'Arkasında plan yok — yönetici tarafından verilmiş ya da deneme',
  'conversions.proof.reason.NO_CHARGE': 'Bu dönem için başarılı tahsilat yok',
  'conversions.proof.reason.PAYMENT_NOT_FOUND':
    'Tahsilatın gösterdiği ödeme bulunamadı',
  'conversions.proof.reason.PAYMENT_NOT_CAPTURED': 'Ödeme hiç tahsil edilmedi',
  'conversions.proof.reason.PAYMENT_REFUNDED': 'Ödeme iade edildi',
  'conversions.proof.reason.NO_CONVERSION':
    'Bu gün ışığı dönemini satın alan tamamlanmış bir geçiş yok',
  'conversions.trail.title': 'Geçiş kaydı',
  'conversions.trail.payment': 'Kart ödemesi',
  'conversions.trail.opened': 'Geçiş başlatıldı',
  'conversions.trail.daylights_taken': 'Gün ışığı alındı',
  'conversions.trail.daylights_returned': 'Gün ışığı iade edildi',
  'conversions.trail.daylights_granted': 'Gün ışığı yüklendi',
  'conversions.trail.daylights_reclaimed': 'Gün ışığı geri alındı',
  'conversions.trail.checkout_started': 'Kart ödemesi başlatıldı',
  'conversions.trail.card_verified': 'Kart doğrulandı (3D Secure)',
  'conversions.trail.payment_captured': 'Ödeme tahsil edildi',
  'conversions.trail.payment_failed': 'Ödeme başarısız',
  'conversions.trail.premium_opened': 'Premium açıldı',
  'conversions.trail.premium_ended': 'Premium sonlandırıldı',
  'conversions.trail.completed': 'Tamamlandı',
  'conversions.trail.failed': 'Başarısız',
  'conversions.trail.expired': 'Yarıda bırakıldı',
  'conversions.paymentStatus.pending': 'Bekliyor',
  'conversions.paymentStatus.authenticated': 'Kart doğrulandı',
  'conversions.paymentStatus.captured': 'Tahsil edildi',
  'conversions.paymentStatus.settled': 'Tamamlandı',
  'conversions.paymentStatus.failed': 'Başarısız',
  'conversions.paymentStatus.refunded': 'İade edildi',
  'conversions.paymentStatus.partially_refunded': 'Kısmen iade edildi',

  'audit.title': 'İşlem kaydı',
  'audit.description':
    'Bir yöneticinin yaptığı her değişiklik ve gerekçe olarak yazdığı şey',
  'audit.filter.operator': 'Yönetici',
  'audit.filter.operatorPlaceholder': 'Kullanıcı adı',
  'audit.filter.entity': 'Neye dokunuldu',
  'audit.filter.user': 'Etkilenen cihaz',
  'audit.entity.all': 'Hepsi',
  'audit.entity.user': 'Hesaplar',
  'audit.entity.wallet': 'Cüzdanlar',
  'audit.entity.alarm': 'Alarmlar',
  'audit.entity.alarm_event': 'Talep kararları',
  'audit.entity.reward_scheme': 'Ödül şemaları',
  'audit.entity.payout_run': 'Dağıtımlar',
  'audit.entity.notification_rule': 'Bildirim durumları',
  'audit.entity.notification_template': 'Şablonlar',
  'audit.entity.notification_campaign': 'Gönderimler',
  'audit.entity.blacklist': 'Susturulan kullanıcılar',
  'audit.emptyTitle': 'Kayıt yok',
  'audit.emptyDescription': 'Bu konsoldan yapılan her değişiklik buraya düşer.',
  'audit.userPrefix': 'kullanıcı',
  'audit.from': '{ip} adresinden',
  'audit.valueNone': 'yok',
  'audit.valueYes': 'evet',
  'audit.valueNo': 'hayır',

  // ── Kitle filtreleri ──────────────────────────────────────────────────────
  'filters.name': 'Ad',
  'filters.nameHint': 'Önce ad, sonra soyad',
  'filters.namePlaceholder': 'Ali Yılmaz',
  'filters.search': 'Cihaz kimliği şununla başlar',
  'filters.platform': 'Platform',
  'filters.subscribed': 'Abone',
  'filters.freeTrial': 'Ücretsiz deneme',
  'filters.pushReady': 'Bildirime hazır',
  'filters.activeAlarms': 'Aktif alarm',
  'filters.hasActiveAlarms': 'Aktif alarmı var',
  'filters.noActiveAlarms': 'Aktif alarmı yok',
  'filters.hasToken': 'Anahtarı var',
  'filters.noToken': 'Anahtarı yok',
  'filters.fewer': 'Daha az koşul',
  'filters.more': 'Daha çok koşul',
  'filters.clearAll': 'Hepsini temizle',
  'filters.group.where': 'Nerede oldukları',
  'filters.countries': 'Ülkeler',
  'filters.countriesHint': 'İki harfli kodlar, virgülle ayrılmış',
  'filters.languages': 'Diller',
  'filters.languagesHint': 'Virgülle ayrılmış',
  'filters.accountActive': 'Hesap aktif',
  'filters.attestation': 'Cihaz doğrulaması',
  'filters.attestation.verified': 'Doğrulandı',
  'filters.attestation.unverified': 'Kontrol edilemedi',
  'filters.attestation.failed': 'Başarısız',
  'filters.attestation.unknown': 'Hiç kontrol edilmedi',
  'filters.appVersion': 'Uygulama sürümü',
  'filters.appVersionHint': '1.4, 1.4.2 ve derlemelerini de kapsar',
  'filters.group.hold': 'Ellerinde ne var',
  'filters.balanceMin': 'Bakiye en az',
  'filters.balanceMax': 'Bakiye en çok',
  'filters.spendableMin': 'Harcanabilir en az',
  'filters.spendableMinHint': 'Bakiye eksi kilitli tutar',
  'filters.spendableMax': 'Harcanabilir en çok',
  'filters.earnedMin': 'Toplam kazanç en az',
  'filters.earnedMax': 'Toplam kazanç en çok',
  'filters.group.use': 'Nasıl kullanıyorlar',
  'filters.alarmsMin': 'Alarm sayısı en az',
  'filters.alarmsMax': 'Alarm sayısı en çok',
  'filters.enabledAlarmsMin': 'Aktif alarm en az',
  'filters.enabledAlarmsMax': 'Aktif alarm en çok',
  'filters.wonMin': 'Kazanılan görev en az',
  'filters.missedMin': 'Kaçırılan görev en az',
  'filters.wakeRateMin': 'Uyanma oranı en az (%)',
  'filters.wakeRateMinHint':
    'Hiçbir şeyi sonuçlanmamış kullanıcılar hiçbir sınıra uymaz',
  'filters.wakeRateMax': 'Uyanma oranı en çok (%)',
  'filters.group.when': 'En son ne zaman göründüler',
  'filters.inactiveMin': 'En az şu kadar sessiz (gün)',
  'filters.inactiveMinHint': 'Sabit bir tarihin aksine yarın da doğru kalır',
  'filters.inactiveMax': 'En çok şu kadar sessiz (gün)',
  'filters.registeredAfter': 'Şu tarihten sonra kaydolan',
  'filters.registeredBefore': 'Şu tarihten önce kaydolan',
  'filters.summary.everyone': 'Herkes',
  'filters.summary.subscribed': 'abone',
  'filters.summary.notSubscribed': 'abone değil',
  'filters.summary.onFreeTrial': 'ücretsiz denemede',
  'filters.summary.notOnFreeTrial': 'ücretsiz denemede değil',
  'filters.summary.deactivated': 'devre dışı',
  'filters.summary.noPushToken': 'bildirim anahtarı yok',
  'filters.summary.hasActiveAlarms': 'aktif alarmı var',
  'filters.summary.noActiveAlarms': 'aktif alarmı yok',
  'filters.summary.quiet': '{days} gün+ sessiz',
  'filters.summary.spendableMax': 'harcanabilir ≤ {value}',
  'filters.summary.spendableMin': 'harcanabilir ≥ {value}',
  'filters.summary.wakeRateMax': 'uyanma oranı ≤ %{value}',
  'filters.summary.wakeRateMin': 'uyanma oranı ≥ %{value}',
  'filters.summary.liveAlarmsMax': '≤ {value} aktif alarm',
  'filters.summary.missedMin': '{value}+ kaçırılmış',
  'filters.summary.namedUsers': 'adı verilmiş {count} kullanıcı',
  'filters.summary.namedDevices': 'adı verilmiş {count} cihaz',
  'filters.summary.idContains': 'kimlik "{value}" ile başlıyor',
  'filters.summary.name': 'adı "{value}…"',
  'filters.summary.appVersion': 'v{value} sürümünde',

  // ── API'nin İngilizce gönderdiği programlar ───────────────────────────────
  'schedule.Once': 'Bir kez',
  'schedule.Every day': 'Her gün',
  'schedule.Mon': 'Pzt',
  'schedule.Tue': 'Sal',
  'schedule.Wed': 'Çar',
  'schedule.Thu': 'Per',
  'schedule.Fri': 'Cum',
  'schedule.Sat': 'Cmt',
  'schedule.Sun': 'Paz',

  // ── `humanize` üzerinden geçen API değerleri ──────────────────────────────
  'enum.rang': 'Alarm çaldı',
  'enum.dismissed': 'Kapatıldı',
  'enum.reward_earned': 'Görev kazanıldı',
  'enum.reward_missed': 'Görev kaçırıldı',
  'enum.reward_withheld': 'Talep beklemede',
  'enum.ring_timed_out': 'Çözülmeden sustu',
  'enum.created': 'Alarm oluşturuldu',
  'enum.deleted': 'Alarm silindi',
  'enum.rejected': 'Kayıt reddedildi',
  'enum.integrity_failed': 'Cihaz doğrulaması başarısız',

  'enum.reward': 'Ödüllü',
  'enum.infinite': 'Sınırsız',
  'enum.none': 'Yok',
  'enum.math': 'Matematik',
  'enum.scanning': 'Tarama',
  'enum.easy': 'Kolay',
  'enum.medium': 'Orta',
  'enum.hard': 'Zor',

  'enum.verified': 'Doğrulandı',
  'enum.unverified': 'Kontrol edilemedi',
  'enum.failed': 'Başarısız',
  'enum.unknown': 'Hiç kontrol edilmedi',

  'enum.topped_up': 'Yükleme',
  'enum.hold_placed': 'Kilit kondu',
  'enum.hold_released': 'Kilit çözüldü',
  'enum.forfeited': 'Yakıldı',
  'enum.reward_payout': 'Ödül ödemesi',
  'enum.spent': 'Harcandı',
  'enum.adjusted': 'Elle düzeltildi',

  'enum.alarm_scheduled': 'Alarm kuruldu',
  'enum.alarm_schedule_reduced': 'Alarm programı azaltıldı',
  'enum.alarm_disabled': 'Alarm kapatıldı',
  'enum.alarm_deleted': 'Alarm silindi',
  'enum.challenge_won': 'Görev kazanıldı',
  'enum.challenge_missed': 'Görev kaçırıldı',
  'enum.purchase': 'Satın alma',
  'enum.ad_reward': 'Reklam ödülü',
  'enum.reward_share': 'Dağıtım payı',
  'enum.mode_conversion': 'Mod geçişi',
  'enum.reversal': 'Geri alma',
  'enum.admin': 'Yönetici',

  'enum.pending': 'Bekliyor',
  'enum.processing': 'İşleniyor',
  'enum.sending': 'Gönderiliyor',
  'enum.sent': 'Gönderildi',
  'enum.completed': 'Tamamlandı',
  'enum.skipped': 'Atlandı',
  'enum.cancelled': 'İptal edildi',
  'enum.paid': 'Ödendi',

  'enum.draft': 'Taslak',
  'enum.committing': 'Ödeniyor',
  'enum.committed': 'Ödendi',
  'enum.scheduled': 'Zamanlanmış',
  'enum.manual': 'Elle',

  'enum.user': 'Hesap',
  'enum.alarm': 'Alarm',
  'enum.wallet': 'Cüzdan',
  'enum.alarm_event': 'Talep kararı',
  'enum.reward_scheme': 'Ödül şeması',
  'enum.payout_run': 'Dağıtım',
  'enum.notification_template': 'Şablon',
  'enum.notification_rule': 'Bildirim durumu',
  'enum.notification_campaign': 'Gönderim',
  'enum.blacklist': 'Susturulan kullanıcı',
  'enum.system': 'Sistem',

  'enum.user_updated': 'Hesap güncellendi',
  'enum.user_activated': 'Hesap yeniden etkinleştirildi',
  'enum.user_deactivated': 'Hesap devre dışı bırakıldı',
  'enum.user_deleted': 'Hesap silindi',
  'enum.user_restored': 'Hesap geri getirildi',
  'enum.subscription_granted': 'Abonelik verildi',
  'enum.subscription_cancelled': 'Abonelik iptal edildi',
  'enum.balance_credited': 'Kredi eklendi',
  'enum.balance_debited': 'Kredi alındı',
  'enum.balance_forfeited': 'Kilit yakıldı',
  'enum.alarm_updated': 'Alarm güncellendi',
  'enum.alarm_enabled': 'Alarm açıldı',
  'enum.alarm_restored': 'Alarm geri getirildi',
  'enum.claim_approved': 'Talep onaylandı',
  'enum.claim_rejected': 'Talep reddedildi',
  'enum.scheme_created': 'Şema oluşturuldu',
  'enum.scheme_updated': 'Şema güncellendi',
  'enum.scheme_deleted': 'Şema silindi',
  'enum.payout_run_started': 'Dağıtım taslağı oluşturuldu',
  'enum.payout_run_committed': 'Dağıtım ödendi',
  'enum.payout_run_cancelled': 'Dağıtım atıldı',
  'enum.template_created': 'Şablon oluşturuldu',
  'enum.template_updated': 'Şablon güncellendi',
  'enum.template_deleted': 'Şablon silindi',
  'enum.notification_rule_created': 'Durum oluşturuldu',
  'enum.notification_rule_updated': 'Durum güncellendi',
  'enum.notification_rule_deleted': 'Durum silindi',
  'enum.notification_sent': 'Bildirim gönderildi',
  'enum.notification_test_sent': 'Deneme bildirimi gönderildi',
  'enum.blacklist_added': 'Kullanıcı susturuldu',
  'enum.blacklist_removed': 'Kullanıcının susturması kaldırıldı',

  // ── Para çekme ────────────────────────────────────────────────────────────
  'nav.withdrawals': 'Para Çekme',
  'withdrawals.title': 'Para çekme talepleri',
  'withdrawals.description':
    'Kullanıcıların ödenmesini istediği kazançlar. Her transfer elle yapılır — onayla, gönder, sonra dekont referansını kaydet.',
  'withdrawals.filter.status': 'Göster',
  'withdrawals.filter.openOnly': 'Bekleyenler',
  'withdrawals.status.pending': 'Beklemede',
  'withdrawals.status.approved': 'Onaylandı',
  'withdrawals.status.paid': 'Ödendi',
  'withdrawals.status.rejected': 'Reddedildi',
  'withdrawals.status.cancelled': 'İptal edildi',
  'withdrawals.tile.pending': 'İncelenmeyi bekliyor',
  'withdrawals.tile.pendingCaption': 'Henüz kimse bakmadı',
  'withdrawals.tile.approved': 'Transferi bekliyor',
  'withdrawals.tile.approvedCaption': 'Kontrol edildi, henüz gönderilmedi',
  'withdrawals.tile.open': 'Kullanıcılar için tutulan',
  'withdrawals.tile.openCaption': 'Bakiyelerinden çoktan düşüldü',
  'withdrawals.tile.paid': 'Ödenen',
  'withdrawals.tile.paidCaption': 'Tüm zamanlar',
  'withdrawals.emptyTitle': 'Bekleyen bir şey yok',
  'withdrawals.emptyDescription': 'Bir kullanıcı ödeme istediğinde talepler burada görünür.',
  'withdrawals.userId': 'Kullanıcı',
  'withdrawals.openUser': 'Hesabı aç',
  'withdrawals.copyIban': "IBAN'ı kopyala",
  'withdrawals.ibanCopied': 'IBAN kopyalandı',
  'withdrawals.ibanCopyFailed': 'IBAN kopyalanamadı',
  'withdrawals.reviewedBy': 'İnceleyen',
  'withdrawals.reference': 'Transfer referansı',
  'withdrawals.referenceHint': 'Bankanın referansı; sonradan eşleştirmek için.',
  'withdrawals.referencePlaceholder': 'örn. FT26091200123',
  'withdrawals.referenceRequired': 'Önce transfer referansını gir',
  'withdrawals.reasonRequired': 'Neden reddedildiğini yaz — kullanıcı bunu görür',
  'withdrawals.rejectionReason': 'Belirtilen gerekçe',
  'withdrawals.note': 'İç not',
  'withdrawals.action.approve': 'Onayla',
  'withdrawals.action.reject': 'Reddet',
  'withdrawals.action.pay': 'Ödendi olarak işaretle',
  'withdrawals.confirm.approve.title': 'Bu talep onaylansın mı?',
  'withdrawals.confirm.approve.description':
    '{amount} tutarının {iban} hesabına gönderilebileceğini onaylar. Henüz para hareket etmez — transferi yap, sonra ödendi olarak işaretle.',
  'withdrawals.confirm.reject.title': 'Bu talep reddedilsin mi?',
  'withdrawals.confirm.reject.description':
    '{amount} kullanıcıya geri döner. Yazdığın gerekçe kullanıcıya gösterilir.',
  'withdrawals.confirm.pay.title': 'Bu transfer kaydedilsin mi?',
  'withdrawals.confirm.pay.description':
    'Yalnızca {amount} gerçekten {iban} hesabına gönderildiyse.',
  'withdrawals.toast.approve': 'Onaylandı. Transferi yap, sonra ödendi olarak işaretle.',
  'withdrawals.toast.reject': 'Reddedildi — kazanç kullanıcıya geri döndü',
  'withdrawals.toast.pay': 'Ödendi olarak kaydedildi',

  // ── Davetler ──────────────────────────────────────────────────────────────
  'nav.referrals': 'Davetler',
  'referrals.title': 'Davetler',
  'referrals.description':
    'Arkadaşını davet et kampanyaları, kimin hangi kodla katıldığı ve ne ödendiği',
  'referrals.newCampaign': 'Yeni kampanya',
  'referrals.tabsLabel': 'Davet bölümleri',
  'referrals.tab.campaigns': 'Kampanyalar',
  'referrals.tab.redemptions': 'Katılanlar',
  'referrals.tab.rewards': 'Ödüller',
  'referrals.tab.activity': 'Kayıt',

  'referrals.tile.live': 'Yayındaki kampanyalar',
  'referrals.tile.liveCaption': '{scheduled} planlandı · {ended} sona erdi',
  'referrals.tile.joined': 'Katılan arkadaşlar',
  'referrals.tile.joinedCaption': '{pending} kişi ilk yüklemesini bekliyor',
  'referrals.tile.paid': 'Ödenen ödüller',
  'referrals.tile.paidCaption': '{daylights} · {days} Premium günü',
  'referrals.tile.failed': 'Başarısız ödüller',
  'referrals.tile.failedCaption': 'Ödüller sekmesinden yeniden dene',

  'referrals.policy.window': 'Kod, kayıttan sonraki {hours} saat içinde girilebilir',
  'referrals.policy.closed': 'Kod girişi kapalı',
  'referrals.policy.signIn': 'Google/Apple ile giriş zorunlu',
  'referrals.policy.noSignIn': 'Giriş zorunlu değil',
  'referrals.policy.ip': 'bir adresten {hours} saatte en fazla {count}',
  'referrals.policy.noIp': 'adres başına sınır yok',

  'referrals.status.live': 'Yayında',
  'referrals.status.scheduled': 'Planlandı',
  'referrals.status.ended': 'Sona erdi',
  'referrals.audience.all': 'Herkes',
  'referrals.audience.users': 'Seçili hesaplar',
  'referrals.audienceHint.all':
    'Her hesap, kodlar ekranını ilk açtığında kodunu alır.',
  'referrals.audienceHint.users':
    'Yalnızca belirttiğin hesaplar kod alır. Sonradan hesap ekleyebilirsin.',
  'referrals.qualification.signup': 'Arkadaş kodu girdiğinde',
  'referrals.qualification.first_top_up': 'Arkadaşın ilk yüklemesinden sonra',
  'referrals.qualificationHint.signup':
    'Sahte bir hesaba hiçbir maliyeti yok — ödülü küçük tut ya da ilk yüklemeyi seç.',
  'referrals.qualificationHint.first_top_up':
    'Arkadaş, gerçek parayla gün ışığı aldığında sayılır. Güvenli seçim.',
  'referrals.rewardType.daylights': 'Gün ışığı',
  'referrals.rewardType.free_trial': 'Ücretsiz Premium günleri',
  'referrals.reward.trialDays': '{count} gün Premium',

  'referrals.offer.signup': 'Katılan {count} arkadaş getir',
  'referrals.offer.first_top_up': 'İlk yüklemesini yapan {count} arkadaş getir',
  'referrals.offer.limited': 'hesap başına en fazla {count} kez',
  'referrals.offer.limited_one': 'hesap başına bir kez',
  'referrals.offer.unlimited': 'her seferinde, sınırsız',

  'referrals.field.audience': 'Kimler kod alır',
  'referrals.field.qualification': 'Arkadaş ne zaman sayılır',
  'referrals.field.requiredReferrals': 'Gereken arkadaş',
  'referrals.field.rewardType': 'Ödül',
  'referrals.field.reward': 'Ödül',
  'referrals.field.daylights': 'Verilecek gün ışığı',
  'referrals.field.trialDays': 'Verilecek Premium günü',
  'referrals.field.maxRewards': 'Bir hesap kaç kez kazanabilir',
  'referrals.field.startsAt': 'Başlangıç',
  'referrals.field.endsAt': 'Bitiş',
  'referrals.field.codes': 'Kodlar',
  'referrals.field.createdBy': 'Oluşturan',
  'referrals.immediately': 'Hemen',
  'referrals.neverExpires': 'Süresiz',
  'referrals.archived': 'Arşivlenmiş kampanya',
  'referrals.openAccount': 'Hesabı aç',

  'referrals.filter.status': 'Durum',
  'referrals.filter.anyStatus': 'Tüm durumlar',
  'referrals.filter.audience': 'Kitle',
  'referrals.filter.anyAudience': 'Tüm kitleler',
  'referrals.filter.search': 'Ad',
  'referrals.filter.campaign': 'Kampanya',
  'referrals.filter.anyCampaign': 'Tüm kampanyalar',
  'referrals.filter.code': 'Kod',
  'referrals.filter.referrer': 'Davet eden',
  'referrals.filter.friend': 'Arkadaş',
  'referrals.filter.account': 'Hesap',
  'referrals.filter.type': 'Ne oldu',
  'referrals.filter.anyType': 'Hepsi',

  'referrals.campaigns.emptyTitle': 'Henüz kampanya yok',
  'referrals.campaigns.emptyDescription':
    'Kampanya bir tekliftir: N arkadaş getir, ödül kazan. Herkes için ya da birkaç hesap için başlat.',
  'referrals.campaigns.failedBadge': '{count} başarısız',
  'referrals.campaigns.endsOn': 'Bitiş: {date}',
  'referrals.campaigns.endedOn': 'Sona erdi: {date}',
  'referrals.campaigns.joinedLine': '{joined} katıldı · {qualified} sayıldı',
  'referrals.campaigns.rewardsLine': '{count} ödül ödendi',

  'referrals.editor.newTitle': 'Yeni kampanya',
  'referrals.editor.editTitle': '{name} — düzenle',
  'referrals.editor.create': 'Kampanyayı oluştur',
  'referrals.editor.created': 'Kampanya oluşturuldu',
  'referrals.editor.updated': 'Kampanya kaydedildi',
  'referrals.editor.confirmCreate': 'Bu kampanya oluşturulsun mu?',
  'referrals.editor.confirmUpdate': 'Değişiklikler kaydedilsin mi?',
  'referrals.editor.nameHint': 'Kullanıcılar bunu kodlarının yanında görür.',
  'referrals.editor.namePlaceholder': 'Bahar daveti',
  'referrals.editor.descriptionHint':
    'Kullanıcılara adın altında gösterilir. Ne kazanacaklarını ve arkadaşlarının ne yapması gerektiğini yaz.',
  'referrals.editor.descriptionPlaceholder':
    '3 arkadaşını davet et. Her biri ilk gün ışığını satın aldığında 100 gün ışığı kazan.',
  'referrals.editor.section.audience': 'Kim',
  'referrals.editor.section.terms': 'Teklif',
  'referrals.editor.section.window': 'Ne zaman',
  'referrals.editor.audienceFixed': 'Kampanya oluşturulduktan sonra değişmez.',
  'referrals.editor.members': 'Cihaz kimlikleri',
  'referrals.editor.membersHint':
    '{count} / en fazla {max} — her satıra bir tane ya da virgülle ayrılmış.',
  'referrals.editor.membersPlaceholder':
    'Cihaz kimliklerini yapıştır, her satıra bir tane',
  'referrals.editor.membersLater': 'Hesapları kampanya sayfasından ekle ya da kapat.',
  'referrals.editor.termsLocked': 'Şartlar kilitli',
  'referrals.editor.termsLockedHint':
    'Bir kod zaten kullanıldı, bu yüzden teklif sabit — davet edenlere verilen söz bu. Değiştirmek için bu kampanyayı sonlandır ve yenisini başlat.',
  'referrals.editor.requiredHint': 'Ödül başına sayılması gereken arkadaş.',
  'referrals.editor.daylightsHint': 'Davet edenin cüzdanına eklenir.',
  'referrals.editor.trialHint':
    'En fazla 365. Süren denemeye eklenir. Zaten Premium için ödeme yapan hesaplar bir şey kazanmaz — onlara gün ışığı ver.',
  'referrals.editor.maxRewardsHint': 'Güvenli varsayılan: bir kez.',
  'referrals.editor.unlimited': 'Sınırsız — her seferinde öde',
  'referrals.editor.startsHint': 'Boş: hemen başlar.',
  'referrals.editor.endsHint': 'Boş: süresiz.',
  'referrals.editor.error.name': 'Kampanyaya bir ad ver.',
  'referrals.editor.error.required': 'Gereken arkadaş 1–{max} arasında olmalı.',
  'referrals.editor.error.daylights': 'Gün ışığı 1–{max} arasında olmalı.',
  'referrals.editor.error.trialDays': 'Premium günü 1–{max} arasında olmalı.',
  'referrals.editor.error.maxRewards': 'Sınır 1–{max} arasında olmalı ya da sınırsız.',
  'referrals.editor.error.window': 'Kampanya başlamadan bitemez.',
  'referrals.editor.error.endInPast': 'Bitiş gelecekte olmalı.',
  'referrals.editor.error.badIds': 'Cihaz kimliği değil: {ids}',
  'referrals.editor.error.noMembers': 'En az bir cihaz kimliği yapıştır.',
  'referrals.editor.error.tooManyMembers': 'Bir seferde en fazla {max} hesap.',

  'referrals.drawer.loading': 'Kampanya',
  'referrals.drawer.edit': 'Düzenle',
  'referrals.drawer.end': 'Şimdi sonlandır',
  'referrals.drawer.archive': 'Arşivle',
  'referrals.drawer.showJoined': 'Katılanlar',
  'referrals.drawer.showRewards': 'Ödüller',
  'referrals.drawer.showActivity': 'Kayıt',
  'referrals.drawer.codesActive': '{total} koddan {active} tanesi çalışıyor',
  'referrals.drawer.members': 'Bu kampanyadaki hesaplar',
  'referrals.drawer.holders': 'Kodu olan hesaplar',
  'referrals.drawer.holdersHint':
    'Kod, hesap kodlar ekranını ilk açtığında oluşturulur; bu yüzden yalnızca o hesaplar listelenir.',
  'referrals.drawer.addMembers': 'Hesap ekle',
  'referrals.drawer.noCodes': 'Henüz kimsenin kodu yok',
  'referrals.drawer.membersAdded':
    '{added} eklendi · {reactivated} yeniden açıldı · {already} zaten vardı',
  'referrals.drawer.done.end': 'Kampanya sonlandırıldı — kodları artık çalışmıyor',
  'referrals.drawer.done.archive': 'Kampanya arşivlendi',
  'referrals.drawer.confirm.end.title': 'Bu kampanya şimdi sonlandırılsın mı?',
  'referrals.drawer.confirm.end.description':
    'Kodları hemen çalışmayı bırakır. Zaten katılmış arkadaşlar sayılmaya ve davet edene ödül kazandırmaya devam eder. Bitiş tarihini düzenleyerek istediğin zaman yeniden açabilirsin.',
  'referrals.drawer.confirm.end.action': 'Şimdi sonlandır',
  'referrals.drawer.confirm.archive.title': 'Bu kampanya arşivlensin mi?',
  'referrals.drawer.confirm.archive.description':
    'Uygulamadan kalkar, kodları çalışmayı bırakır ve artık hiçbir şey ödenmez — ilk yüklemesini bekleyen arkadaşlar için bile. Geçmişi saklanır.',
  'referrals.drawer.confirm.archive.action': 'Arşivle',
  'referrals.drawer.confirm.members.title': 'Bu kampanyaya hesap ekle',
  'referrals.drawer.confirm.members.description':
    'Her biri kendi kodunu alır. Kodu kapatılmış bir hesap kodunu geri alır.',
  'referrals.drawer.confirm.members.action': 'Hesap ekle',

  'referrals.stat.joined': 'Katılan',
  'referrals.stat.qualified': 'Sayılan',
  'referrals.stat.pending': 'Yükleme bekleyen',
  'referrals.stat.rewards': 'Ödenen ödül',
  'referrals.stat.paid': 'Ödenen',
  'referrals.stat.failed': 'Başarısız ödül',

  'referrals.code.off': 'Kapalı',
  'referrals.code.progress': '{qualified} sayıldı · {pending} bekliyor',
  'referrals.code.disable': 'Kapat',
  'referrals.code.enable': 'Aç',
  'referrals.code.disabled': 'Kod kapatıldı',
  'referrals.code.enabled': 'Kod açıldı',
  'referrals.code.confirmDisable': '{code} kapatılsın mı?',
  'referrals.code.confirmEnable': '{code} yeniden açılsın mı?',
  'referrals.code.confirmDisableDescription':
    'Artık girilemez ve daha fazla kazandırmaz. Seçili hesaplara açık bir kampanyada bu, hesabı kampanyadan çıkarır.',
  'referrals.code.confirmEnableDescription':
    'Yeniden çalışır ve kapalıyken kazandığı her şey şimdi ödenir.',

  'referrals.referralStatus.pending': 'Yükleme bekleniyor',
  'referrals.referralStatus.qualified': 'Sayıldı',
  'referrals.referralStatus.revoked': 'İptal edildi',
  'referrals.redemptions.friend': 'Arkadaş',
  'referrals.redemptions.code': 'Kod',
  'referrals.redemptions.campaign': 'Kampanya',
  'referrals.redemptions.referrer': 'Davet eden',
  'referrals.redemptions.status': 'Durum',
  'referrals.redemptions.redeemedAt': 'Katıldı',
  'referrals.redemptions.qualifiedAt': 'Sayıldı',
  'referrals.redemptions.ip': 'Adres',
  'referrals.redemptions.sameIp': 'Davet edenle aynı IP',
  'referrals.redemptions.revoke': 'İptal et',
  'referrals.redemptions.revoked': 'Davet iptal edildi',
  'referrals.redemptions.confirmTitle': 'Bu davet iptal edilsin mi?',
  'referrals.redemptions.confirmDescription':
    '{code} kodunu kullanan arkadaş, davet edenin ödülüne artık sayılmaz. Ödenmiş bir ödül geri alınmaz — bunun için cüzdanı düzelt.',
  'referrals.redemptions.emptyTitle': 'Henüz kimse kodla katılmadı',
  'referrals.redemptions.emptyDescription':
    'Kod giren her arkadaş, iki hesap ve geldiği adresle birlikte burada görünür.',

  'referrals.rewardStatus.pending': 'Ödeniyor',
  'referrals.rewardStatus.granted': 'Ödendi',
  'referrals.rewardStatus.failed': 'Başarısız',
  'referrals.rewards.referrer': 'Davet eden',
  'referrals.rewards.reward': 'Ödül',
  'referrals.rewards.campaign': 'Kampanya',
  'referrals.rewards.ordinal': 'No.',
  'referrals.rewards.status': 'Durum',
  'referrals.rewards.when': 'Ne zaman',
  'referrals.rewards.retry': 'Yeniden dene',
  'referrals.rewards.retried': 'Ödül ödendi',
  'referrals.rewards.retryFailed': 'Hâlâ başarısız: {reason}',
  'referrals.rewards.confirmTitle': 'Bu ödül yeniden denensin mi?',
  'referrals.rewards.confirmDescription':
    'Davet edene {reward} öder. Bir ödül yalnızca bir kez ödenebilir.',
  'referrals.rewards.emptyTitle': 'Henüz ödül yok',
  'referrals.rewards.emptyDescription':
    'Davet edenin yeterli sayıda arkadaşı sayıldığında ödül burada görünür.',

  'referrals.event.redeemed': 'Kod kullanıldı',
  'referrals.event.denied': 'Kod reddedildi',
  'referrals.event.qualified': 'Arkadaş sayıldı',
  'referrals.event.revoked': 'Davet iptal edildi',
  'referrals.event.reward_granted': 'Ödül ödendi',
  'referrals.event.reward_failed': 'Ödül başarısız',
  'referrals.denial.disabled': 'Kod girişi kapalı.',
  'referrals.denial.invalid_code': 'Böyle bir kod yok ya da kapatılmış.',
  'referrals.denial.campaign_not_live': 'Kampanya başlamamıştı ya da sona ermişti.',
  'referrals.denial.window_closed': 'Hesap, kod giriş süresinden daha eski.',
  'referrals.denial.already_redeemed': 'Hesap zaten bir kod kullanmıştı.',
  'referrals.denial.device_already_redeemed':
    'Bu telefon başka bir hesapla zaten bir kod kullanmıştı.',
  'referrals.denial.self_referral': 'Kendi kodu.',
  'referrals.denial.referrer_unavailable': 'Kodun sahibi devre dışı ya da silinmiş.',
  'referrals.denial.account_inactive': 'Hesap devre dışı.',
  'referrals.denial.integrity_failed':
    'Doğrulama, cihazın ya da uygulamanın orijinal olmadığını söyledi.',
  'referrals.denial.ip_limit_reached': 'Bu adresten süre içinde çok fazla kod girildi.',
  'referrals.activity.daylights': '{count} gün ışığı',
  'referrals.activity.by': '{actor} tarafından',
  'referrals.activity.emptyTitle': 'Henüz bir şey olmadı',
  'referrals.activity.emptyDescription':
    'Kullanılan ya da reddedilen her kod, sayılan her arkadaş ve her ödül buraya düşer.',

  'audit.entity.referral_campaign': 'Davet kampanyaları',
  'audit.entity.referral_code': 'Davet kodları',
  'audit.entity.referral': 'Davetler',
  'audit.entity.referral_reward': 'Davet ödülleri',

  'enum.referral': 'Davet',
  'enum.referral_campaign': 'Davet kampanyası',
  'enum.referral_code': 'Davet kodu',
  'enum.referral_reward': 'Davet ödülü',
  'enum.referral_campaign_created': 'Davet kampanyası oluşturuldu',
  'enum.referral_campaign_updated': 'Davet kampanyası güncellendi',
  'enum.referral_campaign_ended': 'Davet kampanyası sonlandırıldı',
  'enum.referral_campaign_deleted': 'Davet kampanyası arşivlendi',
  'enum.referral_members_added': 'Kampanyaya hesap eklendi',
  'enum.referral_code_disabled': 'Davet kodu kapatıldı',
  'enum.referral_code_enabled': 'Davet kodu açıldı',
  'enum.referral_revoked': 'Davet iptal edildi',
  'enum.referral_reward_retried': 'Davet ödülü yeniden denendi',

  // ── Yedekler ──────────────────────────────────────────────────────────────
  'nav.backups': 'Yedekler',
  'backups.title': 'Yedekler',
  'backups.description':
    'Veritabanının tamamı sıkıştırılıp düzenli olarak Google Drive’a kopyalanır.',
  'backups.action.backupNow': 'Şimdi yedekle',
  'backups.action.running': 'Yedekleniyor…',
  'backups.toast.started': 'Yedekleme başladı — arka planda sürüyor',
  'backups.toast.disconnected': 'Google Drive bağlantısı kaldırıldı',
  'backups.tile.last': 'Son yedek',
  'backups.tile.lastNone': 'Henüz başarılı yedek yok',
  'backups.tile.next': 'Sonraki yedek',
  'backups.tile.nextNone': 'Planlanmadı',
  'backups.tile.nextRunning': 'Şu an çalışıyor',
  'backups.tile.paused': 'Zamanlama durduruldu',
  'backups.tile.stored': 'Drive’da',
  'backups.tile.failures': 'Başarısız',
  'backups.tile.failuresCaption': 'Son 7 gün',
  'backups.running.title': 'Veritabanı yedekleniyor',
  'backups.running.detail': '{file} · {since} başladı',
  'backups.drive.title': 'Google Drive',
  'backups.drive.description': 'Yedeklerin saklandığı yer',
  'backups.drive.notConfigured': 'Sunucuda yapılandırılmamış',
  'backups.drive.notConfiguredDetail':
    'Drive bağlanabilmesi için sunucuda Google OAuth istemcisi ve şifreleme anahtarı tanımlı olmalı:',
  'backups.drive.connected': 'Bağlı',
  'backups.drive.connectedDetail': 'Yedekler bu hesabın Drive’ına yazılır.',
  'backups.drive.account': 'Google hesabı',
  'backups.drive.folder': 'Klasör',
  'backups.drive.connectedAt': 'Bağlanma',
  'backups.drive.openFolder': 'Drive’da aç',
  'backups.drive.switch': 'Başka hesap kullan',
  'backups.drive.disconnect': 'Bağlantıyı kaldır',
  'backups.drive.disconnectTitle': 'Google Drive bağlantısı kaldırılsın mı?',
  'backups.drive.disconnectDetail':
    'Yeniden bir Drive bağlanana kadar yedekleme durur. Drive’daki dosyalar silinmez; aynı hesabı bağlarsanız aynı klasörden devam eder.',
  'backups.drive.revoked': 'Google erişim iznini geri aldı',
  'backups.drive.revokedDetail':
    '{account} hesabının izni kaldırıldı, şu an yedek alınmıyor. Yeniden bağlayın — aynı hesap klasörünü korur.',
  'backups.drive.none': 'Bağlı Drive yok',
  'backups.drive.noneDetail':
    'Bir Google hesabı bağlayın; veritabanı aşağıdaki zamanlamayla o hesabın Drive’ına yedeklenir.',
  'backups.drive.pointScope':
    'Uygulama yalnızca kendi oluşturduğu dosyaları görür; Drive’ın geri kalanı gizli kalır.',
  'backups.drive.pointFolder': 'Drive’da yedekler için ayrı bir klasör oluşturulur.',
  'backups.drive.pointToken':
    'Erişim anahtarı şifreli saklanır; bağlantıyı kaldırmak Google’da da iptal eder.',
  'backups.drive.connect': 'Google Drive bağla',
  'backups.drive.reconnect': 'Yeniden bağla',
  'backups.drive.forget': 'Bu hesabı unut',
  'backups.schedule.title': 'Zamanlama',
  'backups.schedule.description': 'Ne sıklıkla ve kaç tane saklanacağı',
  'backups.schedule.enabled': 'Otomatik yedekleme',
  'backups.schedule.enabledHint': '“Şimdi yedekle” her durumda çalışır.',
  'backups.schedule.interval': 'Sıklık',
  'backups.schedule.retention': 'Saklanacak yedek sayısı',
  'backups.schedule.retentionHint':
    'Yaklaşık {days} günlük geçmiş. Daha eskiler Drive çöp kutusuna gider.',
  'backups.schedule.retentionRange': '{min} ile {max} arasında.',
  'backups.schedule.confirmDetail':
    'Sonraki planlı yedek, son yedekten itibaren sayılır.',
  'backups.interval.hours': 'Her {count} saatte bir',
  'backups.interval.days': 'Her {count} günde bir',
  'backups.interval.weeks': 'Her {count} haftada bir',
  // A bare "Her 1 saatte bir" reads oddly; these say it the way people do.
  'backups.interval.hours_one': 'Saatte bir',
  'backups.interval.days_one': 'Günde bir',
  'backups.interval.weeks_one': 'Haftada bir',
  'backups.history.title': 'Geçmiş',
  'backups.history.description': 'Tüm yedekler, en yenisi üstte',
  'backups.history.all': 'Tümü',
  'backups.history.started': 'Başlangıç',
  'backups.history.status': 'Durum',
  'backups.history.trigger': 'Başlatan',
  'backups.history.duration': 'Süre',
  'backups.history.size': 'Boyut',
  'backups.history.file': 'Dosya',
  'backups.history.open': 'Aç',
  'backups.history.pruned': 'Drive çöp kutusunda',
  'backups.history.emptyTitle': 'Henüz yedek yok',
  'backups.history.emptyDescription':
    'Bir Drive bağlayıp “Şimdi yedekle”ye basın ya da zamanlamayı bekleyin.',
  'backups.status.running': 'Çalışıyor',
  'backups.status.succeeded': 'Saklandı',
  'backups.status.failed': 'Başarısız',
  'backups.trigger.scheduled': 'Zamanlama',
  'backups.trigger.manualBy': '{actor}',
  'backups.callback.working': 'Google Drive bağlanıyor…',
  'backups.callback.connected': 'Google Drive bağlandı: {account}',
  'backups.callback.cancelled': 'Bağlantı Google ekranında iptal edildi',
  'backups.callback.failed': 'Google bağlantıyı tamamlayamadı ({reason})',
  'backups.callback.missing': 'Google’ın yanıtı eksik — yeniden bağlayın',
  'audit.entity.backup': 'Yedekler',
  'enum.backup': 'Yedek',
  'enum.backup_drive_connected': 'Yedek Drive’ı bağlandı',
  'enum.backup_drive_disconnected': 'Yedek Drive bağlantısı kaldırıldı',
  'enum.backup_settings_updated': 'Yedek zamanlaması değişti',
  'enum.backup_run_started': 'Yedek elle başlatıldı',
};
